import React, { useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  withGoogleMap,
  DirectionsRenderer,
  InfoWindow,
  KmlLayer,
  Polyline,
} from "react-google-maps";
import PlacesAutocomplete from "react-places-autocomplete";
import { useEffect } from "react";
import { trkType } from "./MapBox/MapBoxFeatures/CompileProject";
import { ExportProject } from "./MapBox/MapBoxFeatures/ExportProject";

import UserContext from "../../../src/UserContext";
import { CustomNavigation } from "./CustomNavigation/CustomNavigation";
import Indice from "../screens/Indice";
import ContextMenu from "./ContextMenu";
import MapBoxSize from "./MapBox/MapBoxSize";
import CoordinatesAlert from "./CoordinatesAlert";
import CalculateRouteButtons from "./MapBox/CalculateRouteButtons";
import MapBoxOptions from "./MapBox/MapBoxOptions";
import MapBoxFeatures from "./MapBox/MapBoxFeatures/MapBoxFeatures";
import DialogBoxWithConfirmation from "../util/DialogBoxWithConfirmation";
import DialogBox from "../util/DialogBox";
import { LoadingBox } from "../util/LoadingBox";
import { closeLoadingBox } from "../util/LoadingBox";
import { customToastNotify } from "../util/Toast";
import {
  DISTANCE_TO_TRIGGER_AUDIO,
  ICON_INDEX_DELETED,
  iconsPaths,
  getParcoursTravelModes,
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  importantManeuvers,
} from "./GPX/Resources";
import { SendGpx } from "./MapBox/MapBoxFeatures/GpxToServ";
import {
  removeNumber,
  generateRandomNumber,
  isEditableTarget,
} from "../util/Util";
import StepBox from "./StepBox/StepBox";
import "./MapWithMarkers.css";
import { calculateGPXRoute } from "./MapBox/MapBoxFeatures/ImportProject";
import DialogBoxWithFooter from "../util/DialogBoxWithFooter";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "../style/CalculateRouteContainer.css";
import { roadmakerApi, RoadmakerApiError } from "../../api/roadmakerApi";
import {
  extractDetailedTrack,
  encodeDetailedTrack,
  serializeDetailedTrack,
} from "../util/DetailedTrack";
import {
  parseGpxTrack,
  resolveGpxImportMode,
  isExplicitConfirmation,
  hasExistingGpxTrack,
  selectImportedTracksPayload,
} from "./gpxImport";
import {
  flushPoiMediaUploads,
  cleanupOrphanQcmMedia,
  mergePoiMediaUploadResults,
  applyFlushedMediaToMarker,
  selectPoiMediaUploadFailures,
  pendingPoiMediaCount,
  discardPoiMediaUploads,
} from "../../api/poiMediaUploads";
import {
  SAVE_OUTCOMES,
  SAVE_REPORT_KEYS,
  buildSaveReport,
  classifyFlushFailure,
  getSaveRefusalKey,
  isResponseLostFailure,
  summarizeSaveFailures,
} from "../util/saveReport";
import {
  projectSaveSnapshot,
  projectSaveSignature,
  snapshotSignature,
  withSnapshotMarkers,
  withoutSnapshotMarkers,
  hasUnsavedProjectChanges,
} from "./unsavedChanges";

export const markerTypes = {
  origin: "origin",
  destination: "destination",
  step: "step",
  navigation: "navigation",
  information: "information",
  touristic: "touristic",
  structure: "structure",
};

/**
 * Signature STABLE d'un point d'intérêt, identique des deux côtés : le client
 * envoie `id` (horodatage attribué à la création du marqueur) et le serveur le
 * persiste tel quel.
 *
 * Elle sert à RECONNAÎTRE un point déjà créé sur le serveur alors que la
 * réponse à sa création s'est perdue : sans cette reconnaissance, la
 * sauvegarde suivante le recréait à l'identique (doublon visible) et la
 * réconciliation des suppressions le supprimait.
 *
 * Elle est volontairement bâtie sur `id` et NON sur le titre : l'utilisateur
 * peut renommer un point entre deux sauvegardes, ce qui romprait la
 * reconnaissance. Rend `null` si l'identifiant manque, pour ne jamais faire
 * correspondre deux points non identifiables.
 */
export const poiSignature = (poi) => {
  const id = poi && poi.id;
  const projectId = poi && poi.Project_id;
  if (
    id === undefined ||
    id === null ||
    projectId === undefined ||
    projectId === null
  ) {
    return null;
  }
  return `${String(id)}|${String(projectId)}`;
};

/**
 * Retrouve l'identifiant serveur d'un point d'intérêt par sa signature métier
 * (`id` du marqueur et projet). Sert à RECONNAÎTRE un point déjà créé dont la
 * réponse à la création ne s'est jamais affichée côté client.
 *
 * Rend un RÉSULTAT et non un simple identifiant : une lecture IMPOSSIBLE
 * (réponse perdue, refus) doit rester DISTINGUÉE d'une lecture réussie sans
 * correspondance. La première interdit toute décision de création — nous ne
 * savons pas ce que le serveur contient — tandis que la seconde prouve que le
 * point n'existe pas. Retourner `null` dans les deux cas (comportement
 * antérieur) faisait traiter l'ignorance comme une absence et créait un
 * DOUBLON au POST suivant (contre-validation du 16 sept., Spec 2).
 *
 * @returns {Promise<{readable: boolean, serverId: string|null}>}
 * `readable` : la liste serveur a été lue ; `serverId` : identifiant trouvé.
 */
const findServerPoiIdBySignature = async (projectId, signature) => {
  if (!signature) {
    // Pas de signature : aucune reconnaissance possible, mais ce n'est pas
    // une panne de lecture. `readable: false` impose la prudence — dire
    // « lisible sans correspondance » laisserait croire à une absence ÉTABLIE.
    return { readable: false, serverId: null };
  }
  try {
    const serverPois = await roadmakerApi.pois.listByProject(projectId);
    const match = (serverPois || []).find(
      (poi) => poiSignature(poi) === signature
    );
    return {
      readable: true,
      serverId: match ? String(match.serverId) : null,
    };
  } catch (error) {
    console.error(
      "[sauvegarde] lecture des points du projet impossible",
      error
    );
    return { readable: false, serverId: null };
  }
};

// Une écriture est REFUSÉE lorsque le serveur a répondu explicitement un 4xx :
// c'est le seul cas où l'on peut affirmer qu'elle n'a pas abouti. Toute autre
// erreur (coupure réseau, délai dépassé, réponse illisible, exception locale)
// laisse l'issue INCONNUE — l'écriture a pu aboutir avant la coupure.
const isRefusedWrite = (error) =>
  error instanceof RoadmakerApiError &&
  error.status >= 400 &&
  error.status < 500;

/**
 * Crée un point d'intérêt sur le serveur en RÉCONCILIANT les créations dont la
 * réponse s'est perdue.
 *
 * Un point dont la réponse ne s'est jamais affichée peut avoir été créé : le
 * recréer produirait un doublon. Il est donc recherché par sa signature
 * (`poiSignature`) avant tout nouvel envoi, puis de nouveau si la création
 * échoue sans refus explicite — une réponse perdue n'est pas la preuve d'une
 * absence de création.
 *
 * Rend l'identifiant serveur adopté, ou `null` si le point n'existe pas sur le
 * serveur.
 */
const findOrCreatePoi = async (projectId, markerData) => {
  const signature = poiSignature(markerData);
  // RÉCONCILIATION AVANT CRÉATION, seulement si le point est IDENTIFIABLE.
  // Sans signature, aucune réconciliation n'est possible — ni maintenant, ni
  // jamais : bloquer la création ne réduirait aucun risque détectable et
  // casserait l'enregistrement des points non identifiables. La création
  // suit donc son cours (comportement historique).
  if (signature) {
    const known = await findServerPoiIdBySignature(projectId, signature);
    if (known.serverId) {
      return known.serverId;
    }
    // Une lecture IMPOSSIBLE n'est pas une absence : créer sans savoir ce
    // que contient le serveur, c'est risquer un doublon si une création
    // précédente a pu aboutir avant que sa réponse se perde. L'opération est
    // laissée INCONNUE (erreur dédiée) sans émettre de POST : la sauvegarde
    // suivante retentera la lecture (contre-validation du 16 sept., Spec 2).
    if (!known.readable) {
      const error = new Error(
        "Réconciliation impossible : l'état du serveur n'a pas pu être lu."
      );
      error.name = "PoiReconcileUnavailableError";
      throw error;
    }
  }
  try {
    const savedMarker = await roadmakerApi.pois.create(projectId, markerData);
    return savedMarker.serverId;
  } catch (error) {
    // Refus EXPLICITE (4xx) : rien n'a été créé, inutile de relire.
    if (isRefusedWrite(error)) {
      throw error;
    }
    // Issue INCONNUE de la création : relire AVANT d'abandonner — mais
    // seulement si le point est identifiable. Si la relecture échoue elle
    // aussi, l'opération reste inconnue : NE PAS retransmettre aveuglément,
    // le point a pu être créé (doublon).
    if (signature) {
      const recovered = await findServerPoiIdBySignature(projectId, signature);
      if (recovered.serverId) {
        return recovered.serverId;
      }
    }
    throw error;
  }
};

/**
 * Résume les échecs média d'un même point par CATÉGORIE, au lieu de ne
 * conserver que le premier d'entre eux.
 *
 * Deux catégories coexistent : l'ENVOI raté (le fichier n'a pas atteint le
 * serveur) et le NETTOYAGE raté d'un média remplacé (le nouveau média est
 * publié, l'ancien reste à supprimer). Elles ne disent pas la même chose à
 * l'utilisateur : un envoi raté signifie qu'un fichier n'existe QUE dans la
 * mémoire du navigateur et qu'un rechargement le perdra. Les réduire à la
 * première erreur rencontrée faisait disparaître cet avertissement.
 *
 * @returns {{ upload: {count: number, error: Error}|null,
 *             replacement: {count: number, error: Error}|null }}
 */
const summarizePoiMediaFailureKinds = (failures) => {
  const summary = { upload: null, replacement: null };
  (failures || []).forEach((failure) => {
    if (!failure) {
      return;
    }
    const kind = classifyFlushFailure(failure);
    if (kind === "replacement") {
      summary.replacement = summary.replacement || {
        count: 0,
        error: failure.error,
      };
      summary.replacement.count += 1;
    } else {
      summary.upload = summary.upload || { count: 0, error: failure.error };
      summary.upload.count += 1;
    }
  });
  return summary;
};

/**
 *  Création de l'objet MapWithAMarker qui est la carte avec les marqueurs
 * @param {object} props
 * @param {object} projectDatas
 *
 */

export const MapWithAMarker = withGoogleMap((props, projectDatas, userData) => (
  <GoogleMap
    center={props.position}
    zoom={props.zoom}
    onClick={props.onMapClick}
    options={{
      styles: props.poiLabels,
      gestureHandling: "greedy",
      scaleControl: true,
    }}
  >
    {props.infoCustomNavig ? (
      <InfoWindow
        position={{
          lat: parseFloat(props.mapPosition.lat),
          lng: parseFloat(props.mapPosition.lng),
        }}
        onCloseClick={props.fermerInfoWindow}
      >
        <div>{props.infoCustomNavig}</div>
      </InfoWindow>
    ) : null}

    {props.markers.map((marker) => (
      <Marker
        key={marker.id}
        position={{ lat: marker.lat, lng: marker.lng }}
        icon={
          marker.type === markerTypes.information && marker.subType === ""
            ? props.markerIcons["Info"]
            : marker.type === markerTypes.information
            ? props.markerIcons[removeNumber(marker.subType)]
            : props.markerIcons[marker.type]
        }
        draggable={true}
        onDragEnd={(event) => props.onMarkerDragEnd(marker.id, event)}
        subType={marker.subType}
        Project_id={projectDatas._id}
        onClick={(event) => props.onMarkerClick(marker.id, event)}
        animation={
          marker.id === props.selectedMarkerId &&
          window.google.maps.Animation.BOUNCE
        }
      />
    ))}

    {props.directionsResponse && !props.kmlFile && (
      <DirectionsRenderer
        directions={props.directionsResponse}
        suppressMarkers={props.markers}
        routeIndex={props.selectedRoute}
        panel={document.getElementById("directions-panel")}
        onRouteChange={props.handleRouteChange}
      />
    )}

    {/* Tracé principal importé (GPX remplacement) : dessiné dès qu'aucun
        rendu Google n'est actif. Couleur du MODE DU PARCOURS (trkType —
        voiture/velo/rando), la même que le GPX exporté et le mobile. */}
    {!props.directionsResponse && !props.kmlFile && props.mainTrack?.length > 1 && (
      <Polyline
        path={props.mainTrack}
        options={{
          strokeColor:
            trkType(props.projectType) || "#0000FF",
          strokeOpacity: 0.8,
          strokeWeight: 5,
          clickable: false,
        }}
      />
    )}

    {/* Traces complémentaires importées (GPX complémentaire) : dessinées en
        plus du tracé principal, couleur distincte. */}
    {(props.importedTracks || []).map(
      (track) =>
        track?.points?.length > 1 && (
          <Polyline
            key={track.id}
            path={track.points}
            options={{
              strokeColor: "#EC5B14",
              strokeOpacity: 0.9,
              strokeWeight: 4,
              clickable: false,
            }}
          />
        )
    )}

    {props.kmlFile && (
      <KmlLayer
        key={props.kmlLayerKey}
        url={props.kmlFile}
        options={{
          suppressInfoWindows: true,
        }}
      />
    )}
  </GoogleMap>
));

export default function MapWithMarker({
  projectDatas,
  userData,
  titleOfProject,
  userPack,
  setVisibleComponent,
  unsavedChangesRef,
}) {
  /* CONSTANTES UTILISÉS PAR LE COMPOSANT */
  const initialPosition = { lat: DEFAULT_LATITUDE, lng: DEFAULT_LONGITUDE };
  const initialZoom = 7;
  const ergonomicZoom = 13;
  const notSelectedButtonStyle = "m-2 btn btn-light";
  const selectedButtonStyle = "m-2 btn btn-success";

  const showAllLabels = [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "transit.station",
      elementType: "all",
      stylers: [{ visibility: "on" }],
    },
  ];
  const hideAllLabels = [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit.station",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
  ];

  const StartAndEndIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.PI_structure;
  const informationIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.PI_standard;
  const panoramaIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.Panorama;
  const parkingIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.Parking;
  const rechargeIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.Recharge;
  const repasIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.Repas;
  const toiletteIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.Toilette;
  const stepIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.Step;
  const touristicIconPath =
    iconsPaths.rootFolder + projectDatas.projectType + iconsPaths.Touristic;

  const markerIcons = {
    origin: StartAndEndIconPath,
    destination: StartAndEndIconPath,
    Info: informationIconPath,
    Parking: parkingIconPath,
    Panorama: panoramaIconPath,
    Recharge: rechargeIconPath,
    Repas: repasIconPath,
    Toilette: toiletteIconPath,
    step: stepIconPath,
    navigation: "poi-inter.png",
    touristic: touristicIconPath,
  };

  const mapRef = useRef(null);
  const [markers, setMarkers] = useState([]);
  const [originMarker, setOriginMarker] = useState({});
  const [destinationMarker, setDestinationMarker] = useState({});
  const [stepMarkers, setStepMarkers] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState("");
  const [selectedMarkerType, setSelectedMarkerType] = useState("");
  const [selectedRoute, setSelectedRoute] = useState(0);
  const [trkseg, setTrkseg] = useState([]); // pour les points du tracé
  const trksegRef = useRef([]);
  // Le tracé n'est envoyé que s'il a été RECALCULÉ dans cette session.
  // Défaut confirmé par la revue du 15 septembre : le PUT du projet
  // transportait systématiquement `trkseg: serializeDetailedTrack(trkseg)`.
  // Un tracé jamais calculé (origine/destination absentes, KML chargé,
  // calcul en échec) partait donc en tableau VIDE et écrasait sur le serveur
  // un tracé pourtant valide. L'état en mémoire ne suffit pas à décider : au
  // chargement, `fetchData` met `polyline_result` dans `polyRef` SANS alimenter
  // `trkseg`, qui reste vide alors que le serveur détient un tracé.
  const [, setIsTrackDirty] = useState(false);
  const trackVersionRef = useRef(0);
  const initialCustomIndicationsEditedRef = useRef(false);
  // Verrou de sauvegarde : bloque les clics répétés et le second appel
  // concurrent (le bouton reste cliquable pendant toute la durée du PUT).
  const savingRef = useRef(false);
  // Suppressions demandées mais NON confirmées par le serveur : seul un échec
  // de la tentative de suppression est enregistré ici. Une suppression
  // réussie n'a plus besoin d'être rejouée, et un point créé dont la réponse
  // s'est perdue n'y figure jamais.
  const selectedMarkersToDeleteRef = useRef([]);
  // Instantané des données telles qu'enregistrées (chargement, puis chaque
  // sauvegarde sans échec, moins les POI dont la suppression est confirmée) :
  // sert à avertir avant de quitter l'éditeur.
  const savedSnapshotRef = useRef(null);
  // POI (id local) dont la suppression a été confirmée depuis le début de
  // la sauvegarde en cours : exclus de l'instantané pris à sa fin.
  const removedDuringSaveRef = useRef(new Set());
  // VERROUS PAR INTENTION (plan de clôture du 16 sept., §3.3-6) : un DELETE
  // demandé par le bouton et sa REPRISE par Enregistrer ne doivent pas
  // travailler en même temps sur le même identifiant serveur — le perdant
  // écraserait la décision du gagnant sur la file et l'état React. Chaque
  // identifiant en cours de traitement y est posé le temps de l'opération.
  const pendingDeleteLocksRef = useRef(new Set());
  // SUPPRESSIONS CONFIRMÉES NON ENCORE APPLIQUÉES À L'ÉTAT REACT (étape 1 du
  // plan de reprise, §3.3 : suppressions MULTIPLES dans un même cycle).
  // Chaque finalisation retranche SON identifiant ; le recalcul filtre TOUS
  // ceux qui restent — sinon le premier recalcul repartait d'un état encore
  // peuplé par les autres points supprimés dans la même sauvegarde. Un
  // updater APPLIQUÉ vide l'ensemble des identifiants qu'il a réellement
  // retirés (l'état React les a absorbés : plus besoin de les refiltrer).
  const confirmedRemovalsNotYetAppliedRef = useRef(new Set());
  // Le chargement initial des POI ne constitue pas une modification de
  // l'itinéraire : le tracé déjà persisté doit rester intact jusqu'à une
  // action explicite de l'utilisateur.
  const skipRouteCalculationAfterLoadRef = useRef(false);
  const isTrackDirtyRef = useRef(false);
  /**
   * Calculs déclenchés par une modification de l'itinéraire. L'ensemble
   * distingue les calculs utilisateur des calculs de chargement initial, afin
   * que l'enregistrement puisse attendre les premiers sans bloquer un
   * enregistrement sans modification.
   *
   * @type {React.MutableRefObject<Set<{
   *   promise: Promise<{status: string, error?: Error}>,
   *   started: boolean,
   *   timeoutId?: ReturnType<typeof setTimeout>,
   * }>>}
   */
  const pendingRouteCalculationsRef = useRef(new Set());
  // Trace brute du dernier import GPX (mode historique, alimentait l'export
  // GPX) — conservée pour compatibilité.
  const [importedTrack, setImportedTrack] = useState([]);
  // Traces GPX complémentaires (2026-09-22) : dessinées EN PLUS du tracé
  // principal et persistées dans project.importedTracks. Chaque entrée :
  // {id, name, points:[{lat,lng}]}.
  const [importedTracks, setImportedTracks] = useState(
    Array.isArray(projectDatas.importedTracks)
      ? projectDatas.importedTracks
      : []
  );
  // Vrai tant que le tracé PRINCIPAL vient d'un import GPX en remplacement :
  // bloque le calcul Google (il écraserait un itinéraire qu'il ne connaît
  // pas). Retombe à false au rechargement si le projet n'en porte pas.
  const [hasImportedMainTrack, setHasImportedMainTrack] = useState(false);
  // Miroir ref des traces complémentaires : la sauvegarde doit lire l'état
  // LE PLUS RÉCENT même si le handler a été capturé par un rendu antérieur
  // (fermeture React) — même mécanisme que trksegRef/polyRef.
  const importedTracksRef = useRef(importedTracks);
  importedTracksRef.current = importedTracks;
  // Dernières traces confirmées par un PUT réussi : n'avance jamais sur échec.
  const savedImportedTracksRef = useRef(importedTracks);
  // Idem pour le flag tracé importé (lu par la sauvegarde).
  const hasImportedMainTrackRef = useRef(false);
  hasImportedMainTrackRef.current = hasImportedMainTrack;
  const [customNavigationPoints, setCustomNavigationPoints] = useState([]); // les pts de navigation d'un itinéraire
  const [customIndicationsEdited, setCustomIndicationsEdited] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({});
  const [infoCustomNavig, setInfoCustomNavig] = useState(""); // type étape qui s'affiche dans infoWindow
  const [mapPosition, setMapPosition] = useState(initialPosition);
  const [zoom, setZoom] = useState(initialZoom);
  const [mapDimensions, setMapDimensions] = useState({
    height: "96%",
    width: "95%",
  });
  const [poiLabels, setPoilLabels] = useState(hideAllLabels);
  const [placeInput, setPlaceInput] = useState();
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [travelMode, setTravelMode] = useState(() => {
    try {
      const travelModes = getParcoursTravelModes();
      return (
        window.google?.maps?.TravelMode?.[
          travelModes[projectDatas.projectType]
        ] ?? null
      );
    } catch (e) {
      return null;
    }
  });

  const [optimizeWaypoints, setOptimizeWaypoints] = useState(true);
  const [avoidHighways, setAvoidHighways] = useState(true);
  const [classCompilationButton, setClassCompilationButton] =
    useState("invisible");
  const [refreshMap, setRefreshMap] = useState("invisible");
  const [initialButtonNavigationsVisibility, setInitialNavigationButtonClass] =
    useState("invisibleButtons");
  const [customNavigationButtonClass, setCustomNavigationButtonClass] =
    useState(projectDatas.kmlFile ? "customDirections" : "invisibleButtons");
  const [sendData, setSendData] = useState("invisible");
  const [navigationPanelVisibility, setNavigationPanelVisibility] = useState({
    height: "482px",
    width: "100%",
    display: "none",
  });
  const [
    navigationPanelVisibilityIsActif,
    setNavigationPanelVisibilityIsActif,
  ] = useState(false); // False au début
  const [
    customNavigationPanelVisibilityIsActif,
    setCustomNavigationPanelVisibilityIsActif,
  ] = useState(false); // False au début

  const [customNavigationPanelVisibility, setCustomNavigationPanelVisibility] =
    useState({ height: "482px", width: "100%", display: "none" });

  const toggleCustomNavigationPanel = () => {
    // Si le panneau custom est activé, on cache le panneau normal
    setCustomNavigationPanelVisibilityIsActif((prevState) => !prevState);
    setCustomNavigationPanelVisibility((prevState) => ({
      ...prevState,
      display: prevState.display === "block" ? "none" : "block",
    }));

    // Désactiver l'autre panneau si le custom est activé
    if (!customNavigationPanelVisibilityIsActif) {
      setNavigationPanelVisibilityIsActif(false);
      setNavigationPanelVisibility((prevState) => ({
        ...prevState,
        display: "none",
      }));
    }
  };

  const toggleNavigationPanel = () => {
    // Si le panneau normal est activé, on cache le panneau custom
    setNavigationPanelVisibilityIsActif((prevState) => !prevState);
    setNavigationPanelVisibility((prevState) => ({
      ...prevState,
      display: prevState.display === "block" ? "none" : "block",
    }));

    // Désactiver l'autre panneau si le normal est activé
    if (!navigationPanelVisibilityIsActif) {
      setCustomNavigationPanelVisibilityIsActif(false);
      setCustomNavigationPanelVisibility((prevState) => ({
        ...prevState,
        display: "none",
      }));
    }
  };

  const [copiedCoordinates, setCopiedCoordinates] = useState(false);
  const [, setPoly] = useState("");
  const polyRef = useRef("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [path, setPath] = useState({});
  const [kmlFile, setKmlFile] = useState(projectDatas.kmlFile);
  const [urlKmlToFetsh, setUrlKmlToFetsh] = useState(
    projectDatas.urlKmlToFetsh
  );
  const [downloadTime, setDownloadTime] = useState(null);
  const [kmlLayerKey, setKmlLayerKey] = useState(0);
  const [showInputKML, setShowInputKML] = useState(false);
  const [showInputGPX, setShowInputGPX] = useState(false);
  const [showInputGPXKml, setShowInputGPXKml] = useState(false);
  const [selectedMarkerToDelete, setSelectedMarkerToDelete] = useState("");

  // const [coordinates, setCoordinates] = useState([]);
  const [showInfoPointMenu, setShowInfoPointMenu] = useState(false);
  const [editedSubType, setEditedSubType] = useState(false);
  const [destinationSameAsOrigin, setDestinationSameAsOrigin] = useState(
    projectDatas.destinationSameAsOrigin
      ? projectDatas.destinationSameAsOrigin
      : false
  );
  const [iconIndexArray, setIconIndexArray] = useState(userData.iconIndexArray);
  const [, setKmlCoordiantes] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { t } = useTranslation();
  const TOURISTIC_POI = "PI touristique";
  const FREE_ACCESS = "Gratuit";
  const MANUAL_TRIGGER = "Manuel";
  const MINIMAL_RADIUS = 10;
  const START_POI = "Point de départ";
  const END_POI = "Point de destination";
  const NAVIGATION_POI = "Point de navigation";
  const INFORMATION_POI = "PI standard";
  const LOGO_CONNEXION_IMG = "/rm_imgs/logo_connexion.png";

  useEffect(() => {
    /**
     * Permet de récupérer les données du fichier KML
     */
    const fetchKMLData = async () => {
      try {
        const response = await fetch(urlKmlToFetsh);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const kmlContent = await response.text();

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(kmlContent, "text/xml");

        const coordinatesNode = xmlDoc.querySelectorAll("coordinates");

        const extractedCoordinates = [];

        coordinatesNode.forEach((coord) => {
          const coordText = coord.textContent.trim();
          const coordList = coordText.split(" ");
          coordList.forEach((c) => {
            const [lon, lat] = c.split(",");
            extractedCoordinates.push({
              latitude: parseFloat(lat),
              longitude: parseFloat(lon),
            });
          });
        });
        console.log(kmlFile);
        setKmlCoordiantes(extractedCoordinates);
      } catch (error) {
        console.error("Erreur lors de la récupération du fichier KML", error);
      }
    };
    fetchKMLData();
  }, [kmlFile]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Suppr dans un champ de saisie (dont l'éditeur de texte riche du POI,
      // en contentEditable) efface un caractère, jamais le POI.
      if (isEditableTarget(event.target)) return;
      if (
        event.key === "Delete" &&
        selectedMarkerId &&
        selectedMarkerType !== markerTypes.destination &&
        selectedMarkerType !== markerTypes.origin &&
        !isInputFocused
      ) {
        selectedMarkerId &&
          handleMarkerDelete(selectedMarkerId, selectedMarkerToDelete);
        setSelectedMarkerId("");
        setSelectedMarkerToDelete("");
        setSelectedMarkerType("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedMarkerId, isInputFocused]);

  useEffect(() => {
    if (kmlFile) {
      setDirectionsResponse(null);
      if (!customIndicationsEdited && customNavigationPoints.length === 0) {
        setCustomNavigationPoints([]); // seulement si pas d'indications chargées
      }
      setInitialNavigationButtonClass("invisibleButtons");
    }
  }, [kmlFile]);
  /**
   * Permet de récupérer les données du parcours
   */
  async function fetchData() {
    const normalizedCustomIndicationsEdited = Boolean(
      projectDatas.customIndicationsEdited
    );
    setCustomIndicationsEdited(normalizedCustomIndicationsEdited);
    initialCustomIndicationsEditedRef.current =
      normalizedCustomIndicationsEdited;
    //récuperer le point de départ et d'arrivée du projet
    if (projectDatas.originPOI) {
      setOriginMarker(projectDatas.originPOI);
      if (projectDatas.destinationPOI)
        setDestinationMarker(projectDatas.destinationPOI);
    }

    //Récupération des POI du projet afin de les afficher sur la carte dès le chargement de la page
    const projectPois = await roadmakerApi.pois.listByProject(
      projectDatas._id
    );
    const projectPoisArray = projectPois.sort(
      (a, b) => a.dateOfCreation - b.dateOfCreation
    ).map((marker) => {
      let icon =
        marker.type === markerTypes.information
          ? markerIcons[marker.subType]
          : markerIcons[marker.type];
      return {
        _id: marker._id,
        id: new Date(marker.id).getTime(),
        lat: parseFloat(marker.position.lat),
        lng: parseFloat(marker.position.lng),
        title: marker.title,
        type: marker.type,
        subType: marker.subType,
        // l'icone du marker qui apparait sur la carte
        icon: icon,
        stopover: marker.stopover,
        Project_id: marker.Project_id,
        serverId: marker.serverId,
        description: marker.description,
        image: marker.image,
        // médias persistés côté serveur (ADR 0005) : DTO sans clé physique
        media: marker.media || [],
        // l'icone du PI touristique
        iconImage: marker.iconImage,
        iconName: marker.iconName,
        markerToDownload: marker.markerToDownload,
        video: marker.video,
        audio: marker.audio,
        checkAcces: marker.checkAcces,
        imageName: marker.imageName,
        videoName: marker.videoName,
        iconNameWithoutBorder: marker.iconNameWithoutBorder,
        audioName: marker.audioName,
        mainResource: marker.mainResource,
        mainImage: marker.mainImage,
        resourceArray: marker.resourceArray,
        triggerType: marker.triggerType,
        triggerDistance: marker.triggerDistance,
        openQuestionArray: marker.openQuestionArray,
        qcmArray: marker.qcmArray,
        qcmImageArray: marker.qcmImageArray,
        url: marker.url,
        draggable: true,
        onClick: () => {
          handleMarkerClick(marker);
        },
      };
    });
    const hasOrigin = projectPoisArray.some(
      (marker) => marker.type === markerTypes.origin
    );
    const hasDestination = projectPoisArray.some(
      (marker) => marker.type === markerTypes.destination
    );
    skipRouteCalculationAfterLoadRef.current = hasOrigin && hasDestination;
    setMarkers(projectPoisArray);
    const loadedNavigationPoints = projectDatas.customNavigationPoints
      ? JSON.parse(projectDatas.customNavigationPoints)
      : [];
    if (projectDatas.customNavigationPoints)
      setCustomNavigationPoints(loadedNavigationPoints);
    savedSnapshotRef.current = projectSaveSnapshot({
      markers: projectPoisArray,
      originMarker: projectDatas.originPOI,
      destinationMarker: projectDatas.originPOI
        ? projectDatas.destinationPOI
        : undefined,
      customNavigationPoints: loadedNavigationPoints,
      customIndicationsEdited: normalizedCustomIndicationsEdited,
    });
  }

  /**
   * Vrai si quitter l'éditeur ferait perdre des modifications : données
   * enregistrables différentes de la dernière référence, tracé, traces
   * complémentaires, suppressions ou médias encore à envoyer.
   */
  const hasUnsavedChanges = () =>
    hasUnsavedProjectChanges({
      baselineSignature: snapshotSignature(savedSnapshotRef.current),
      currentSignature: projectSaveSignature({
        markers,
        originMarker,
        destinationMarker,
        customNavigationPoints,
        customIndicationsEdited,
      }),
      trackDirty: isTrackDirtyRef.current,
      importedTracksChanged:
        selectImportedTracksPayload(
          importedTracksRef.current,
          savedImportedTracksRef.current
        ) !== undefined,
      pendingRemovals: selectedMarkersToDeleteRef.current.filter(Boolean)
        .length,
      pendingMedia: markers.some((marker) => pendingPoiMediaCount(marker.id) > 0),
    });
  // Toujours la version du dernier rendu, lue par Home avant de quitter et
  // par l'avertissement du navigateur (fermeture ou rechargement de l'onglet).
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  useEffect(() => {
    const check = () => hasUnsavedChangesRef.current();
    if (unsavedChangesRef) unsavedChangesRef.current = check;
    const handleBeforeUnload = (event) => {
      // Home remet la référence à null quand l'utilisateur a déjà confirmé
      // le départ (déconnexion) : pas de seconde demande.
      if (unsavedChangesRef && unsavedChangesRef.current !== check) return;
      if (!check()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (unsavedChangesRef && unsavedChangesRef.current === check) {
        unsavedChangesRef.current = null;
      }
    };
  }, [unsavedChangesRef]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      //recuperer la localisation si l'utilisateur le permet
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          function (position) {
            setLatitude(position.coords.latitude);
            setLongitude(position.coords.longitude);
            setZoom(ergonomicZoom);
          },
          function (error) {
            console.log(
              "La géolocalisation est désactivée en raison de l'autorisation refusée."
            );
            setLatitude(DEFAULT_LATITUDE);
            setLongitude(DEFAULT_LONGITUDE);
            setZoom(initialZoom);
          }
        );
      } else {
        console.log(
          "La géolocalisation n'est pas prise en charge par votre navigateur."
        );
        setLatitude(DEFAULT_LATITUDE);
        setLongitude(DEFAULT_LONGITUDE);
        setZoom(initialZoom);
      }

      fetchData();
      isInitialMount.current = false;
      if (projectDatas.polyline_result) {
        polyRef.current = projectDatas.polyline_result;
        setPoly(projectDatas.polyline_result);
      }
      // Rechargement d'un projet au tracé importé (GPX remplacement) :
      // réactiver le rendu Polyline et bloquer le calcul Google. Le signe
      // est le FLAG PERSISTÉ (importedMainTrack), jamais une heuristique —
      // un tracé Google sauvegardé a aussi un trkseg et doit rester
      // recalculable après glissement d'un marqueur.
      if (projectDatas.importedMainTrack === true) {
        if (Array.isArray(projectDatas.trkseg) && projectDatas.trkseg.length) {
          trksegRef.current = projectDatas.trkseg;
          setTrkseg(projectDatas.trkseg);
        }
        setHasImportedMainTrack(true);
      }
    } else {
      calculateRoute();
    }
  }, [
    travelMode,
    avoidHighways,
    optimizeWaypoints,
    editedSubType,
    kmlFile,
  ]);

  useEffect(() => {
    // Ajoutez un écouteur d'événements de clic sur le document entier
    document.addEventListener("click", handleClickOutsideMap);

    // Retirez l'écouteur d'événements lorsque le composant est démonté
    return () => {
      document.removeEventListener("click", handleClickOutsideMap);
    };
  }, []);

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };
  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  const handleSelect = (value) => {
    setPlaceInput(value);
    handleFocus();
  };

  const showNavigationPanel = () => {
    setCustomNavigationPanelVisibility({
      height: "482px",
      width: "100%",
      display: "none",
    });
    setNavigationPanelVisibility({
      height: "482px",
      width: "100%",
      display: "block",
    });
  };

  const showCustomNavigationPanel = () => {
    setNavigationPanelVisibility({
      height: "482px",
      width: "100%",
      display: "none",
    });
    setCustomNavigationPanelVisibility({
      height: "482px",
      width: "100%",
      display: "block",
      overflow: "auto",
    });
  };

  /**
   * Permet d'afficher le menu d'information d'un marker
   * @param {integer} id
   */
  const handleMarkerClick = (id) => {
    const newMarkers = markers.map((marker) => {
      console.log(marker);
      if (marker.id === id) {
        const markerClick = {
          ...marker,
          showButton: true, // Ajouter une propriété pour afficher le bouton de suppression
        };
        setSelectedMarkerId(markerClick.id);
        setSelectedMarkerToDelete(markerClick._id);
        setSelectedMarkerType(markerClick.type);
        return markerClick;
      }
      return marker;
    });
    setMarkers(newMarkers);

    const currentScroll = window.scrollY;
    const threshold = 150; //seuil verticale

    if (currentScroll < threshold) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }
  };

  /**
   * FINALISATION PARTAGÉE d'une suppression CONFIRMÉE (plan de clôture du
   * 16 sept., §3.3-4/5). Appelée aussi bien par le bouton (`handleMarkerDelete`)
   * que par la reprise d'Enregistrer (`updateProjectMarkers`), elle applique
   * le retrait local au DERNIER état React connu, par identifiant serveur
   * stable : un rafraîchissement média ou un ancien instantané ne peut pas
   * réintroduire le marqueur. Les effets irréversibles locaux — retrait de
   * l'icône touristique, purge des fichiers EN ATTENTE de ce point — ne
   * surviennent qu'ici, après confirmation, jamais pendant l'incertitude.
   */
  const finalizeConfirmedPoiRemoval = (serverId, markerSnapshot) => {
    const id = String(serverId);
    // 1. File de reprise : l'intention est satisfaite.
    selectedMarkersToDeleteRef.current = selectedMarkersToDeleteRef.current.filter(
      (queued) => String(queued) !== id
    );
    // 2. État React, au DERNIER état connu, par fonction de mise à jour : le
    //    retrait ne dépend pas de l'instantané pris au moment de la demande.
    //
    //    ORDONNANCEMENT DE L'UPDATER (vérification étape 1 du 17 sept.) : ne
    //    JAMAIS retirer d'informations dans l'updater. Les fermetures des
    //    recalculs en vol utilisent leur PROPRE tableau `markers` capturé —
    //    un identifiant purgé par l'updater redevenait visible pour tout
    //    calcul issu d'une fermeture ancienne (updaters immédiats : le
    //    premier point supprimé réapparaissait dans la dernière requête).
    //    L'ensemble ne fait donc que CROÎTRE pendant le cycle : chaque
    //    suppression confirmée y reste jusqu'à la FIN de la sauvegarde ;
    //    il est purgé uniquement quand la persistance est terminée (voir
    //    `persistProjectData`), jamais avant. Le filtrage étant idempotent,
    //    un état React déjà propre ne change pas.
    confirmedRemovalsNotYetAppliedRef.current.add(id);
    // La suppression est enregistrée côté serveur : elle ne compte pas comme
    // une modification à sauvegarder.
    removedDuringSaveRef.current.add(markerSnapshot.id);
    if (savedSnapshotRef.current) {
      savedSnapshotRef.current = withoutSnapshotMarkers(
        savedSnapshotRef.current,
        [markerSnapshot.id]
      );
    }
    const withoutRemovedPoi = (list) =>
      (list || []).filter(
        (marker) =>
          !confirmedRemovalsNotYetAppliedRef.current.has(String(marker.serverId)) &&
          String(marker.serverId) !== id &&
          marker.id !== markerSnapshot.id
      );
    setMarkers((previousMarkers) => withoutRemovedPoi(previousMarkers));
    // 3. Icône touristique : libérée seulement maintenant (auparavant dès la
    //    tentative, donc même en cas d'échec — effet irréversible anticipé).
    deleteFromIconIndexArray(markerSnapshot);
    // 4. Fichiers EN ATTENTE de ce point : la suppression étant confirmée,
    //    ils n'ont plus de destinataire. Les files des AUTRES marqueurs ne
    //    sont jamais touchées (discard est par identifiant local).
    discardPoiMediaUploads(markerSnapshot.id, () => true);
    // 5. Sélection : ne pas laisser pointer un marqueur disparu.
    if (selectedMarkerId === markerSnapshot.id) {
      setSelectedMarkerId("");
      setSelectedMarkerToDelete("");
    }
    // 6. Recalcul depuis l'état de référence FILTRÉ (même règle que l'updater,
    //    point 2) : valable pour TOUT type participant au parcours — y compris
    //    `navigation`, que l'effet surveillant `origin`/`destination`/
    //    `step` ne couvre PAS et dont la suppression ne déclenche donc pas
    //    de recalcul correctif par l'effet. Les réponses arrivant hors ordre
    //    restent ignorées par la génération de calcul (`routeGenerationRef`).
    calculateRoute(withoutRemovedPoi(markers));
  };

  /**
   * Permet de supprimer un marker après confirmation de l'utilisateur
   * @param {integer} id
   */
  const handleMarkerDelete = async (id, idToDelete) => {
    console.log(isInputFocused);

    if (isInputFocused) {
      return;
    }
    const confirmDelete = await DialogBoxWithConfirmation({
      title: "Confirmation",
      text: t("deleteStepText"),
      icon: "warning",
      cancelButtonText: t("no"),
      confirmButtonText: t("yes"),
    });

    if (confirmDelete) {
      if (idToDelete) {
        const serverId = String(idToDelete);
        // Verrou d'intention (§3.3-6) : si une REPRISE par Enregistrer traite
        // déjà ce point, ne pas lancer un second DELETE concurrent dessus.
        if (pendingDeleteLocksRef.current.has(serverId)) {
          DialogBox({
            text: t("errorDeleteStep"),
            icon: "error",
            confirmButtonText: "OK",
          });
          setSelectedMarkerId("");
          setSelectedMarkerToDelete("");
          return;
        }
        pendingDeleteLocksRef.current.add(serverId);
        // Marqué AVANT l'appel réseau : si la réponse se perd, le serveur a
        // peut-être supprimé le point sans que le client le sache, et une
        // absence d'accusé ne doit jamais être assimilée à une suppression
        // volontaire. Ce repère est retiré dès que la suppression est
        // CONFIRMÉE et sert de preuve de volonté à la reprise.
        if (
          !selectedMarkersToDeleteRef.current.some((q) => String(q) === serverId)
        ) {
          selectedMarkersToDeleteRef.current.push(idToDelete);
        }
        // Si le marker est enregistré, on appelle le back pour le supprimer
        try {
          await roadmakerApi.pois.remove(idToDelete);
          // Confirmation reçue : finalisation partagée (retrait React au
          // dernier état, file, icône, files d'attente, sélection, recalcul).
          const snapshot =
            markers.find((m) => String(m.serverId) === serverId) ||
            { id, serverId: idToDelete };
          finalizeConfirmedPoiRemoval(serverId, snapshot);
          DialogBox({
            text: t("successDeleteStep"),
            icon: "success",
            confirmButtonText: "OK",
          });
        } catch (error) {
          // Le marqueur reste VISIBLE et l'intention reste en file : la
          // prochaine sauvegarde refera la tentative (§3.2). Aucun effet
          // irréversible local n'a eu lieu.
          DialogBox({
            text: t("errorDeleteStep"),
            icon: "error",
            confirmButtonText: "OK",
          });
        } finally {
          pendingDeleteLocksRef.current.delete(serverId);
          // NETTOYAGE SYMÉTRIQUE de la purge de fin de `persistProjectData` :
          // ce verrou libéré, si AUCUNE autre intention n'est en vol, plus
          // aucune opération ne détient d'ancien instantané — l'ensemble des
          // « confirmées non appliquées » peut être purgé. Sinon il reste au
          // prochain cycle (sur-filtrage sûr : jamais une réintroduction).
          if (pendingDeleteLocksRef.current.size === 0) {
            confirmedRemovalsNotYetAppliedRef.current.clear();
          }
        }
      } else {
        setMarkers((markers) => markers.filter((marker) => marker.id !== id));
        calculateRoute();
        DialogBox({
          text: t("successDeleteStep"),
          icon: "success",
          confirmButtonText: "OK",
        });
      }
    }
    setSelectedMarkerId("");
    setSelectedMarkerToDelete("");
  };

  /**
   * Permet de mettre le marker d'origine à la position du marker de destination
   * @param {array} newMarkers
   * @param {array} markers
   * @returns {array} updatedMarkers
   */
  const makeOriginSameAsDestination = (newMarkers, markers) => {
    const updatedMarkers = newMarkers.map((marker) => {
      if (marker.type === markerTypes.origin) {
        const destinationMarker = newMarkers.find(
          (m) => m.type === markerTypes.destination
        );
        const editedMarker = {
          ...marker,
          lat: destinationMarker.lat,
          lng: destinationMarker.lng,
        };
        return editedMarker;
      }
      return marker;
    });

    if (updatedMarkers !== markers) {
      setMarkers(updatedMarkers);
    }

    return updatedMarkers;
  };
  /**
   * Permet de mettre à jour la position d'un marker après un drag
   * @param {integer} id - id du marker
   * @param {event} event - événement de drag
   */
  const handleMarkerDragEnd = (id, event) => {
    let editedMarker;
    const newMarkers = markers.map((marker) => {
      if (marker.id === id) {
        editedMarker = {
          ...marker,
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };

        if (marker.id === originMarker.id) {
          setOriginMarker(editedMarker);
        } else if (marker.id === destinationMarker.id) {
          setDestinationMarker(editedMarker);
          if (destinationSameAsOrigin) setOriginMarker(editedMarker); // (à améliorer) Ligne ajoutée car dans le cas destinationSameAsOrigin on ne peut toucher que la destination
        } else {
          const wptMarker = markers.find((m) => m.id === editedMarker.id);
          if (wptMarker) {
            const index = markers.indexOf(wptMarker);
            markers[index] = editedMarker;
          }
        }

        return editedMarker;
      }

      return marker;
    });

    setMarkers(newMarkers);

    if (destinationSameAsOrigin) {
      const updatedMarkers = makeOriginSameAsDestination(newMarkers, markers);
      setMarkers(updatedMarkers);
    }
  };

  /**
   * Permet l'affichage du menu à l'endroit du clic sur la carte
   * @param {event} event - événement de clic sur la carte
   */
  const handleMapClick = (event) => {
    if (event.pixel) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMenuVisible(true);
      setShowInfoPointMenu(false);
      setMenuPosition({
        x: event.pixel.x,
        y: event.pixel.y,
        lat: lat,
        lng: lng,
      });
      setInfoCustomNavig("");
    }
  };

  /**
   * Permet de choisir le type de marker à créer
   * @param {string} type - type de marker
   */
  const handleMenuItemClick = async (type) => {
    let stopover = true;
    if (type === markerTypes.navigation) stopover = false;
    // creation dun nouveau marker avec tous
    let newMarker = {
      id: Date.now(),
      lat: menuPosition.lat,
      lng: menuPosition.lng,
      type: type,
      icon: "",
      title:
        type === markerTypes.origin
          ? START_POI
          : type === markerTypes.destination
          ? END_POI
          : type === markerTypes.step
          ? NAVIGATION_POI
          : type === markerTypes.information
          ? INFORMATION_POI
          : type === markerTypes.touristic
          ? TOURISTIC_POI
          : null,
      description:
        type === markerTypes.structure
          ? "Reprise route. Activer le parcours en cliquant sur l'icône 'panneaux' en bas à gauche."
          : "",
      // création du sybType par défault à améliorer pour une meilleur maintenabilité
      subType: "",
      Project_id: projectDatas._id,
      stopover: stopover,
      onClick: () => {
        handleMarkerClick(newMarker);
      },
    };
    if (type === markerTypes.destination) {
      let newDestinationMarker = {
        ...newMarker,
        title: END_POI,
      };
      setDestinationMarker(newDestinationMarker);
      setMarkers([...markers, newDestinationMarker]);
    }
    if (type === markerTypes.information) {
      let newInformationMarker = {
        ...newMarker,
        title: markerTypes.information,
      };
      setMarkers([...markers, newInformationMarker]);
    }
    if (type === markerTypes.structure) {
      let newStructureMarker = {
        ...newMarker,
        title: "CIRCUIT" + projectDatas.name,
      };
      setMarkers([...markers, newStructureMarker]);
    }
    if (type === markerTypes.touristic) {
      let newTouristicMarker = {
        ...newMarker,
        title: TOURISTIC_POI,
        checkAcces: FREE_ACCESS,
        triggerType: MANUAL_TRIGGER,
        triggerDistance: MINIMAL_RADIUS,
      };
      setMarkers([...markers, newTouristicMarker]);
    } else if (type === markerTypes.origin) {
      const origin = {
        ...newMarker,
        type: markerTypes.origin,
        title: START_POI,
      };
      setOriginMarker(origin);
      const confirm = await DialogBoxWithConfirmation({
        title: "Confirmation",
        text: t("fastForwardQuestion"),
        icon: "question",
        cancelButtonText: t("no"),
        confirmButtonText: t("yes"),
      });
      if (confirm) {
        setDestinationSameAsOrigin(true);
        const destination = {
          ...newMarker,
          id: Date.now(),
          type: markerTypes.destination,
          title: END_POI,
        };
        setDestinationMarker(destination);
        setMarkers([...markers, origin, destination]);
      } else {
        setMarkers([...markers, origin]);
      }
    } else setMarkers([...markers, newMarker]);
  };

  /**
   * Permet de faire le focus sur le lieu recherché
   */
  const handleFocus = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: placeInput }, (results, status) => {
      if (status === "OK") {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        setMapPosition({ lat, lng });
        setMenuVisible(true);
        setZoom(ergonomicZoom);
        setInfoCustomNavig("");
      }
    });
  };

  /**
   * Choix du mode d'itinéraire
   * @param {event} e - événement de clic sur le bouton
   */
  const handleTravelMode = (e) => {
    console.log("jest: Fonction de handleTravelmode est appelé");
    const mode = e.currentTarget.id;
    setTravelMode(mode);
  };

  /**
   * Permet de calculer l'itinéraire
   *
   */
  // GÉNÉRATIONS DE CALCUL (contre-validation du 17 sept., Spec P2) : les
  // réponses DirectionsService ne sont pas ordonnées — une réponse ANCIENNE
  // peut arriver après une nouvelle (suppression de POI puis recalcul) et
  // rétablir une géométrie passant par le point supprimé, marquée « à
  // envoyer ». Chaque calcul prend un numéro de génération ; en revenant,
  // une réponse dont la génération n'est plus la DERNIÈRE est ignorée.
  const routeGenerationRef = useRef(0);

  const calculateRouteWithoutTracking = async (
    routeMarkers = markers,
    { markDirty = true, ignoreImportedMainTrack = false } = {}
  ) => {
    if (kmlFile) {
      console.log("❌ Route non calculée : un fichier KML est déjà chargé.");
      return { status: "skipped" };
    }
    // Tracé principal importé (GPX remplacement) : le calcul Google
    // remplacerait un itinéraire qu'il ne connaît pas par son propre
    // chemin. Seul le bouton « Revenir au calcul Google » (confirmé) lève
    // cette garde via `ignoreImportedMainTrack`.
    if (hasImportedMainTrack && !ignoreImportedMainTrack) {
      console.log(
        "❌ Route non calculée : le tracé principal vient d'un import GPX."
      );
      return { status: "skipped" };
    }
    console.log("jest: Appeller la fonction calculateRoute");
    const normalizedRouteMarkers = Array.isArray(routeMarkers)
      ? routeMarkers.filter(Boolean)
      : [];
    // Cette génération identifie le calcul : toute réponse revenant pour une
    // génération dépassée sera ignorée (voir routeGenerationRef ci-dessus).
    const generation = ++routeGenerationRef.current;
    const directionsService = new window.google.maps.DirectionsService();
    const origin = normalizedRouteMarkers.find(
      (marker) => marker.type === markerTypes.origin
    );
    const destination = normalizedRouteMarkers.find(
      (marker) => marker.type === markerTypes.destination
    );

    if (
      !origin ||
      !destination ||
      !Number.isFinite(Number(origin.lat)) ||
      !Number.isFinite(Number(origin.lng)) ||
      !Number.isFinite(Number(destination.lat)) ||
      !Number.isFinite(Number(destination.lng))
    ) {
      console.warn("❌ Route non calculée : origine ou destination absente.");
      return { status: "skipped" };
    }

    const nextStepMarkers = normalizedRouteMarkers
      .filter(
        (marker) =>
          marker.type === markerTypes.step ||
          marker.type === markerTypes.navigation
      )
      .filter(
        (marker) =>
          Number.isFinite(Number(marker.lat)) &&
          Number.isFinite(Number(marker.lng))
      )
      .sort((a, b) => a.dateOfCreation - b.dateOfCreation)
      .map((marker) => {
        return {
          location: new window.google.maps.LatLng(marker.lat, marker.lng),
          stopover: marker.stopover,
        };
      });

    setStepMarkers(nextStepMarkers);

    let results;

    try {
      results = await directionsService.route({
        origin: new window.google.maps.LatLng(origin.lat, origin.lng),
        destination: new window.google.maps.LatLng(
          destination.lat,
          destination.lng
        ),
        waypoints: nextStepMarkers,
        optimizeWaypoints: optimizeWaypoints,
        travelMode: travelMode,
        avoidHighways: avoidHighways,
        provideRouteAlternatives: true,
      });
    } catch (error) {
      console.error("❌ Erreur lors du calcul de l'itinéraire", error);
      return { status: "failed", error };
    }

    // RÉPONSE OBSOLÈTE : un calcul plus récent a été lancé pendant l'attente
    // (le tableau des marqueurs a changé — suppression, déplacement).
    // Appliquer ce résultat réécrirait `trkseg` et `isTrackDirty` avec une
    // géométrie périmée, par exemple un tracé passant par un POI supprimé,
    // prête à être enregistrée. La réponse est ignorée ENTIÈREMENT.
    if (generation !== routeGenerationRef.current) {
      console.warn(
        "⚠️ Réponse de calcul ignorée : une demande plus récente est en cours."
      );
      return { status: "superseded" };
    }

    setDirectionsResponse(results);

    // Obtention de la nouvelle polyligne de l'itinéraire.
    // La géométrie complète est extraite des pas (legs[].steps[].path) :
    // overview_polyline/overview_path sont simplifiées par Google et
    // produisaient de grandes lignes droites sur les longs trajets.
    const detailedTrack = extractDetailedTrack(results.routes[selectedRoute]);
    if (detailedTrack.length === 0) {
      console.warn(
        "⚠️ Tracé détaillé indisponible : repli sur overview_path simplifié."
      );
    }
    const newPolyline =
      encodeDetailedTrack(detailedTrack) ||
      results.routes[selectedRoute].overview_polyline;
    const legs = results.routes[selectedRoute].legs;
    const totalDistance = legs
      .map((leg) => leg.distance.value)
      .reduce((a, b) => a + b);

    const totalDuration = results.routes[selectedRoute].legs
      .map((leg) => leg.duration.value)
      .reduce((a, b) => a + b);

    function formatDuration(duration) {
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      const seconds = duration % 60;
      const hoursString = hours > 0 ? `${hours}h` : "";
      const minutesString = minutes > 0 ? `${minutes}min` : "";
      const secondsString = seconds > 0 ? `${seconds}s` : "";
      return `${hoursString} ${minutesString} ${secondsString}`.trim();
    }
    // Problème ici car ça calcule uniquement le premier segment (faire une boucle sur tous les legs dans routes[0])
    setDistance("Distance : " + totalDistance / 1000 + " km");
    setDuration(t("duration") + " : " + formatDuration(totalDuration));

    // Affection des points du tracé : géométrie complète (pas de Google),
    // même type d'objets (LatLng) que l'ancien overview_path ; repli sur
    // celui-ci si la réponse n'a pas de pas exploitables.
    const nextTrack =
      detailedTrack.length > 0
        ? detailedTrack
        : results.routes[selectedRoute].overview_path;
    // Affection des points de navigation
    let steps = [];
    results.routes[selectedRoute].legs.forEach((leg) => {
      leg.steps.forEach((step) => {
        if (importantManeuvers.includes(step.maneuver)) {
          steps.push({
            ...step,
            stepNumber: steps.length + 1,
            distanceIsModified: false,
            checkAudio: true,
          });
        } else {
          console.log(
            `🟡 Étape ignorée - maneuver: ${
              step.maneuver ?? "undefined"
            } | instruction: ${step.instructions}`
          );
        }
      });
    });

    if (!customIndicationsEdited && !customNavigationPoints.length) {
      setCustomNavigationPoints(steps);
    }

    polyRef.current = newPolyline;
    setPoly(newPolyline);
    trksegRef.current = nextTrack;
    setTrkseg(nextTrack);
    // Marqueur posé UNIQUEMENT en cas de calcul réussi : c'est la seule
    // preuve qu'un tracé neuf existe en mémoire et qu'il doit être envoyé.
    if (markDirty) {
      trackVersionRef.current += 1;
      isTrackDirtyRef.current = true;
      setIsTrackDirty(true);
    }
    setClassCompilationButton("btn btn-outline-success mt-4");
    setInitialNavigationButtonClass("initialDirections");
    setCustomNavigationButtonClass("customDirections");
    setSendData("btn btn-outline-primary mt-4");
    return { status: "success" };
  };

  const normalizeRouteCalculationResult = (calculation) =>
    Promise.resolve(calculation)
      .then((result) => result || { status: "success" })
      .catch((error) => {
        console.error(
          "❌ Erreur inattendue lors du calcul de l'itinéraire",
          error
        );
        return { status: "failed", error };
      });

  const registerPendingRouteCalculation = (calculation) => {
    const pendingCalculation = {
      started: true,
      promise: null,
    };
    const normalizedCalculation = normalizeRouteCalculationResult(calculation);
    pendingCalculation.promise = normalizedCalculation.finally(() => {
      pendingRouteCalculationsRef.current.delete(pendingCalculation);
    });
    pendingRouteCalculationsRef.current.add(pendingCalculation);
    return pendingCalculation.promise;
  };

  const calculateRoute = (routeMarkers = markers, options = {}) => {
    const calculation = calculateRouteWithoutTracking(routeMarkers, options);
    if (options.markDirty === false) {
      return normalizeRouteCalculationResult(calculation);
    }
    return registerPendingRouteCalculation(calculation);
  };

  const scheduleRouteCalculation = (routeMarkers, options = {}) => {
    const markDirty = options.markDirty !== false;

    if (!markDirty) {
      const timeoutId = setTimeout(() => {
        void calculateRoute(routeMarkers, options);
      }, 100);
      return () => clearTimeout(timeoutId);
    }

    let resolveCalculation;
    const pendingCalculation = {
      started: false,
      promise: new Promise((resolve) => {
        resolveCalculation = resolve;
      }),
      timeoutId: null,
    };
    const settleCalculation = (result) => {
      pendingRouteCalculationsRef.current.delete(pendingCalculation);
      resolveCalculation(result);
    };

    pendingCalculation.timeoutId = setTimeout(() => {
      pendingCalculation.started = true;
      const calculation = calculateRouteWithoutTracking(routeMarkers, options);
      normalizeRouteCalculationResult(calculation).then(settleCalculation);
    }, 100);
    pendingRouteCalculationsRef.current.add(pendingCalculation);

    return () => {
      if (pendingCalculation.started) {
        return;
      }
      clearTimeout(pendingCalculation.timeoutId);
      settleCalculation({ status: "cancelled" });
    };
  };

  const waitForPendingRouteCalculations = async () => {
    const failures = [];
    while (pendingRouteCalculationsRef.current.size > 0) {
      const pendingCalculations = Array.from(
        pendingRouteCalculationsRef.current
      );
      const results = await Promise.all(
        pendingCalculations.map(({ promise }) => promise)
      );
      results.forEach((result) => {
        if (result && result.status === "failed") {
          failures.push(result.error);
        }
      });
    }
    return failures;
  };

  /**
   * Permet de cache le menu d'information d'un marker en cas de clic en dehors de la carte
   * @param {event} event - événement de clic en dehors de la carte
   */
  const handleClickOutsideMap = (event) => {
    // Vérifiez si l'événement de clic a été déclenché à l'extérieur de l'élément de carte
    if (mapRef.current && !mapRef.current.contains(event.target)) {
      setMenuVisible(false);
      // setShowInfoPointMenu(false);
    }
  };
  /**
   * Supprime le marker du tableau iconIndexArray
   * @param {object} marker - marker concerné
   */
  // Todo : à fusionner avec la fonction dans Hub.js
  const deleteFromIconIndexArray = (marker) => {
    // Par sécurité, on ne supprime pas la valeur par défaut
    if (iconIndexArray.length < 2) return;
    if (marker.type === markerTypes.touristic) {
      // On incrémente l'index uniquement quand le marker à sauvegarder contient une icone
      if (marker.iconName && marker.iconName.length > 0) {
        // On vérifie que l'icone n'existe pas déjà dans la liste du User
        if (iconIndexArray.includes(marker.iconName)) {
          const iconNameIndex = iconIndexArray.indexOf(marker.iconName);
          if (iconNameIndex !== -1) {
            iconIndexArray[iconNameIndex] =
              ICON_INDEX_DELETED + marker.iconName;
          }
        }
      }
    }
  };
  /**
   * Met à jour les  markers du projet
   *
   * DEUX GARDE-FOUS, nés de la revue du 16 septembre :
   *
   * 1. RÉCONCILIATION DES CRÉATIONS. Un point créé dont la réponse s'est
   *    perdue n'a pas reçu de `serverId` : il était donc recréé au prochain
   *    enregistrement (doublon) tout en étant absent des identifiants connus,
   *    ce qui le faisait supprimer. Il est maintenant reconnu par sa signature
   *    métier (`poiSignature`, envoyée à la création) et adopté au lieu d'être
   *    recréé.
   * 2. SUPPRESSION RAISONNÉE. Un point serveur n'est supprimé que si sa
   *    suppression a été DEMANDÉE dans cette session. Une absence d'accusé de
   *    réception n'est jamais assimilée à une suppression volontaire.
   *
   * @param {string} projectId - id du projet
   * @param {array} currentMarkers - tableau des markers actuels
   * @param {function} onFailure - signalement d'un échec (bilan de sauvegarde)
   * @param {object} [options] - `options.removedServerIds` : identifiants de
   *   points dont la suppression a été demandée sans confirmation du serveur.
   * @param {object} [options.finalize] - finalisation locale d'une suppression
   *   confirmée (retrait React au dernier état, icône, files d'attente).
   */
  const updateProjectMarkers = async (
    currentMarkers,
    projectId,
    onFailure,
    { removedServerIds = [], finalize = null } = {}
  ) => {
    const projectMarkers = await roadmakerApi.pois.listByProject(projectId);
    // Identifiants MÉTIER présents sur le serveur : ils servent à reconnaître
    // un point déjà créé dont la réponse a été perdue.
    const serverSignatures = new Map();
    projectMarkers.forEach((marker) => {
      const key = poiSignature(marker);
      if (key) {
        serverSignatures.set(key, String(marker.serverId));
      }
    });
    // Adopter AVANT toute suppression : un point reconnu par sa signature ne
    // doit pas être traité comme un point serveur orphelin.
    currentMarkers.forEach((marker) => {
      if (marker && marker.serverId === undefined) {
        const key = poiSignature(marker);
        const adopted = key ? serverSignatures.get(key) : null;
        if (adopted) {
          marker.serverId = adopted;
        }
      }
    });
    // Suppressions effectivement demandées : une suppression VOLONTAIRE est
    // tentée et confirmée dans la foulée (`handleMarkerDelete`) ; seul un
    // échec de cette tentative est rejoué ici. Sans cette liste, l'ancienne
    // règle supprimait tout point serveur absent des identifiants locaux, donc
    // aussi un point tout juste créé dont la réponse s'était perdue.
    const requestedRemovals = new Set(
      (removedServerIds || []).filter(Boolean).map((id) => String(id))
    );
    // Identifiants des suppressions CONFIRMÉES pendant cette réconciliation :
    // seuls eux sont retirés de la file de reprise. Un échec du DELETE y
    // reste — la sauvegarde suivante devra le RETENTER (contre-validation du
    // 16 sept., Spec 3 : la file était vidée d'avance, la seconde sauvegarde
    // devenait silencieuse sans avoir effectué la suppression).
    const confirmedRemovals = [];
    await Promise.all(projectMarkers.map(async (marker) => {
      // vérifier si le marqueur appartient au projet actuel
      if (String(marker.Project_id) === String(projectId)) {
        // L'INTENTION EST L'AUTORITÉ (plan de clôture du 16 sept., §3.3-2).
        // L'ancienne garde `!markerExists` exigeait que le marqueur ait
        // DISPARU localement ; or le bouton CONSERVE le marqueur visible tant
        // que la suppression n'est pas confirmée — la reprise ne s'exécutait
        // donc jamais dans le parcours réel. La présence du marqueur à
        // l'écran ne doit pas empêcher la reprise d'une demande explicite.
        // La règle historique inverse (supprimer tout POI absent localement)
        // n'est PAS restaurée : sans intention, jamais de suppression.
        if (requestedRemovals.has(String(marker.serverId))) {
          const serverId = String(marker.serverId);
          // VERROU D'INTENTION (§3.3-6) : le bouton traite déjà ce point —
          // ne pas lancer un second DELETE concurrent sur la même intention.
          if (pendingDeleteLocksRef.current.has(serverId)) {
            return;
          }
          pendingDeleteLocksRef.current.add(serverId);
          try {
            await roadmakerApi.pois.remove(marker.serverId);
            confirmedRemovals.push(serverId);
            if (finalize) {
              const snapshot = currentMarkers.find(
                (m) => String(m.serverId) === serverId
              );
              finalize(serverId, snapshot || { id: marker.id, serverId });
            }
          } catch (err) {
            // Échec auparavant SILENCIEUX : le point restait sur le serveur
            // alors que l'utilisateur le croyait supprimé, et il réapparaissait
            // au rechargement. Le bilan de sauvegarde doit le dire — et la
            // suppression RESTE dans la file de reprise.
            if (onFailure) {
              onFailure(`de la suppression du point ${marker.id}`, err);
            } else {
              console.error(err);
            }
          } finally {
            pendingDeleteLocksRef.current.delete(serverId);
          }
        }
      }
    }));
    // RÉCONCILIATION D'UN DELETE À RÉPONSE PERDUE (§3.4) : la liste serveur
    // ci-dessus est une lecture COMPLÈTE du projet (contrat `listByProject` :
    // pas de pagination). Un identifiant demandé qui n'y figure PAS est donc
    // établi ABSENT du serveur : la suppression a pu aboutir avant que sa
    // réponse se perde. Finaliser localement sans émettre de nouveau DELETE,
    // pour que l'intention ne devienne pas éternelle. Une lecture en échec
    // aurait levé plus haut : elle n'entre pas ici (aucune absence déduite
    // d'une ignorance). Un 404 isolé n'est jamais utilisé comme preuve —
    // seule la lecture complète fait foi.
    for (const serverId of requestedRemovals) {
      if (confirmedRemovals.includes(serverId)) {
        continue;
      }
      const stillOnServer = projectMarkers.some(
        (marker) => String(marker.serverId) === serverId
      );
      if (!stillOnServer) {
        confirmedRemovals.push(serverId);
        if (finalize) {
          const snapshot = currentMarkers.find(
            (m) => String(m.serverId) === serverId
          );
          finalize(serverId, snapshot || { id: serverId, serverId });
        }
      }
    }
    return { confirmedRemovals };
  };

  /**
   * Écrit sur le serveur tout ce que la sauvegarde doit persister, et rend le
   * bilan des opérations. Aucun affichage ici : la boîte de chargement, le
   * verrou de sauvegarde et le message sont gérés par `sendDataToServ`, qui
   * garantit leur libération quoi qu'il arrive.
   */
  const persistProjectData = async () => {
    // GARDE DÉPART/ARRIVÉE (2026-09-22) : ces deux POI sont nécessaires à
    // RoadPlayer (déclencheurs, guidage) — sans eux le parcours existe côté
    // éditeur mais pas côté participant. Le refus est explicite et précoce :
    // rien n'est envoyé au serveur tant que les deux points manquent.
    // Les points existent SOIT comme états dédiés (posés par un calcul, un
    // import GPX, ou chargés du projet) SOIT dans le tableau des marqueurs
    // (flow clic carte) : les deux sources couvrent tous les chemins.
    const refusalKey = getSaveRefusalKey(originMarker, destinationMarker, markers);
    if (refusalKey) {
      return { status: "refused", reason: "missingStartOrEnd", messageKey: refusalKey };
    }
    // BILAN UNIQUE de sauvegarde : on agrège les échecs au lieu de les
    // journaliser séparément. Un échec silencieux laissait croire à une
    // sauvegarde réussie (l'utilisateur perdait son travail au rechargement).
    const saveFailures = [];
    // `kind` sert à composer le message : le tracé est la donnée la plus
    // coûteuse à perdre, les médias viennent ensuite (fichiers de l'utilisateur
    // potentiellement perdus au rechargement), le reste est générique.
    const reportSaveFailure = (scope, error, kind = "other") => {
      saveFailures.push({ scope, error, kind });
      console.error(`[sauvegarde] échec ${scope}`, error);
    };
    // Issue du PUT du projet, du point de vue du CLIENT. `refused` n'est
    // retenu que sur un refus EXPLICITE du serveur (4xx) : une réponse perdue
    // (coupure, délai dépassé, réponse illisible) ne prouve aucune absence
    // d'écriture et doit rester `unknown` — la revue du 15 septembre reproche
    // précisément d'annoncer « non enregistré » sans preuve.
    let trackOutcome = SAVE_OUTCOMES.notAttempted;
    // Les calculs de chargement initial ne sont pas dans cette file : ils
    // doivent rester sans effet sur le garde-fou « aucune modification ».
    const routeCalculationFailures = await waitForPendingRouteCalculations();
    if (routeCalculationFailures.length > 0) {
      trackOutcome = SAVE_OUTCOMES.unknown;
      routeCalculationFailures.forEach((error) => {
        reportSaveFailure(
          "du calcul de l'itinéraire",
          error || new Error("Le calcul de l'itinéraire a échoué."),
          "track"
        );
      });
    }
    const trackDirtyAtSave = isTrackDirtyRef.current;
    const trackVersionAtSave = trackVersionRef.current;
    // Instantané REF des traces complémentaires (fermeture React) : lire
    // l'état courant, pas celui du rendu qui a capturé le handler.
    const importedTracksAtSave = importedTracksRef.current;
    const importedTracksPayload = selectImportedTracksPayload(
      importedTracksAtSave,
      savedImportedTracksRef.current
    );
    const customIndicationsEditedAtSave = customIndicationsEdited;
    const customIndicationsEditedChanged =
      customIndicationsEditedAtSave !==
      initialCustomIndicationsEditedRef.current;
    const navigationPointsChanged =
      trackDirtyAtSave ||
      customIndicationsEditedAtSave ||
      customIndicationsEditedChanged;
    // Copie des marqueurs pour éviter les modifications d'état inattendues
    const currentMarkers = [...markers];
    // Ce qui part au serveur, figé avant l'envoi (les POI sont modifiés sur
    // place) ; devient la référence si la sauvegarde aboutit sans échec.
    const snapshotAtSave = projectSaveSnapshot({
      markers: currentMarkers,
      originMarker,
      destinationMarker,
      customNavigationPoints,
      customIndicationsEdited: customIndicationsEditedAtSave,
    });
    removedDuringSaveRef.current = new Set();
    // SUPPRESSIONS DEMANDÉES : identifiants de points dont l'utilisateur a
    // demandé la suppression sans que le serveur la confirme (coupure pendant
    // le DELETE). Seuls ceux-là sont rejoués par la réconciliation ; un point
    // créé dont la réponse s'est perdue n'est donc jamais supprimé.
    //
    // La file N'EST PLUS VIDÉE ICI (contre-validation du 16 sept., Spec 3) :
    // `updateProjectMarkers` rend la liste des suppressions CONFIRMÉES de CE
    // lot, et seules celles-là sortent de la file après l'appel. Un échec de
    // DELETE y reste donc : la sauvegarde suivante le retentera au lieu de
    // devenir silencieuse. Une demande AJOUTÉE pendant cette sauvegarde n'est
    // pas dans l'instantané `removedServerIds` : elle ne peut pas être perdue
    // par la confirmation du lot en cours.
    const removedServerIds = selectedMarkersToDeleteRef.current.filter(
      Boolean
    );
    // Instantané DÉDUPLIQUÉ des demandes : la même intention peut avoir été
    // poussée deux fois (double clic, reprise). Chaque identifiant ne doit
    // donner qu'UNE tentative par sauvegarde.
    const removalIntentions = new Set(
      removedServerIds.map((id) => String(id))
    );

    // Envoi des mises à jour et des nouveaux marqueurs
    // Les marqueurs dont des médias viennent d'être publiés sont
    // reconstruits de façon immuable afin que la consultation se
    // re-rende sans recharger le parcours (C1, parcours 2026-09-04).
    const refreshedMarkers = [];
    await Promise.all(
      currentMarkers.map(async (marker) => {
        // Champs enregistrés : à refléter dans SAVED_MARKER_FIELDS
        // (unsavedChanges.js), qui détecte les modifications non sauvegardées.
        let markerData = {
          //_id: marker._id,
          id: marker.id,
          position: { lat: marker.lat, lng: marker.lng },
          type: marker.type,
          subType: marker.subType,
          // icone du marker qui apparait sur la carte
          icon: markerIcons[marker.type],
          title: marker.title,
          description: marker.description,
          image: marker.image,
          // icone du marker touristique
          iconImage: marker.iconImage,
          iconName: marker.iconName,
          markerToDownload: marker.markerToDownload,
          video: marker.video,
          audio: marker.audio,
          checkAcces: marker.checkAcces,
          imageName: marker.imageName,
          videoName: marker.videoName,
          iconNameWithoutBorder: marker.iconNameWithoutBorder,
          audioName: marker.audioName,
          stopover: marker.stopover,
          Project_id: marker.Project_id,
          mainResource: marker.mainResource,
          // Lot « image principale » (2026-10-01) : checksum de l'image
          // désignée (ou ""), transmis par la whitelist POI_WRITE_FIELDS.
          mainImage: marker.mainImage,
          resourceArray: marker.resourceArray,
          triggerType: marker.triggerType,
          triggerDistance: marker.triggerDistance,
          openQuestionArray: marker.openQuestionArray,
          qcmArray: marker.qcmArray,
          qcmImageArray: marker.qcmImageArray,
          url: marker.url,
        };

        try {
          // POI EN ATTENTE DE SUPPRESSION (§3.2) : aucun PUT/POST ni upload
          // pour un point dont la suppression est DEMANDÉE mais non
          // confirmée — éditer ou renvoyer des fichiers à un point que
          // l'utilisateur veut détruire, c'est travailler contre son
          // intention. Les fichiers en attente de ce point ne sont PAS
          // purgés ici : ils ne le seront qu'à la finalisation (confirmation),
          // et les files des AUTRES marqueurs ne sont jamais touchées.
          const pendingRemoval =
            marker.serverId !== undefined &&
            removalIntentions.has(String(marker.serverId));
          if (pendingRemoval) {
            return;
          }
          if (marker.hasOwnProperty("serverId")) {
            // si le marker a déjà été envoyé au serveur et a reçu un id,
            // on vérifie s'il a bougé par rapport à la dernière position enregistrée
            if (
              markerData.position.lat !== marker.lastLat ||
              markerData.position.lng !== marker.lastLng
            ) {
              // si yes, on envoie une requête de mise à jour
              await roadmakerApi.pois.update(marker.serverId, markerData);
            }
          } else {
            // RÉCONCILIATION DES CRÉATIONS : un point dont la réponse s'est
            // perdue lors d'une sauvegarde précédente existe déjà sur le
            // serveur alors qu'il n'a pas de `serverId` en mémoire. Le recréer
            // produirait un doublon et la réconciliation des suppressions le
            // supprimerait. `findOrCreatePoi` le reconnaît par sa signature
            // (`id` et projet, tous deux envoyés ci-dessus) puis l'adopte.
            marker.serverId = await findOrCreatePoi(
              projectDatas._id,
              markerData
            );
          }
          // Tout POI persistant, y compris s'il n'a pas bougé, peut vider
          // sa file de médias après la sauvegarde. L'ADR 0005 exige ce
          // serverId avant l'écriture binaire.
          const flushedMedia = await flushPoiMediaUploads(
            marker.id,
            projectDatas._id,
            marker.serverId
          );
          // `flushPoiMediaUploads` ne LÈVE PAS : elle rend un résultat par
          // pièce, avec `error` pour un envoi raté et `replacementError` pour
          // un nettoyage raté. Sans ce contrôle, un POI enregistré sans ses
          // médias passait pour un succès complet (défaut relevé par la revue
          // du 15 septembre). Les fichiers restent en file : la relance les
          // reprend, mais l'utilisateur doit savoir que ce n'est pas fini.
          const uploadFailures = selectPoiMediaUploadFailures(flushedMedia);
          // TOUTES les catégories d'échec du même point sont rapportées, pas
          // seulement la première (`uploadFailures[0]`) : quand un nettoyage
          // ET un envoi échouaient pour le même point, seul le nettoyage était
          // affiché — le fichier resté en mémoire n'était alors pas signalé,
          // alors que c'est justement lui qu'un rechargement détruit. Défaut
          // relevé par la revue du 16 septembre.
          if (uploadFailures.length > 0) {
            const { upload, replacement } = summarizePoiMediaFailureKinds(
              uploadFailures
            );
            if (upload) {
              // `classified` distingue un envoi raté (le fichier n'est QUE
              // dans la mémoire du navigateur) d'un nettoyage raté APRÈS un
              // envoi réussi (le nouveau média est enregistré, l'ancien reste
              // à supprimer). Les deux étaient fusionnés en « média ».
              //
              // Une issue INCONNUE (réponse perdue après un envoi peut-être
              // accepté) ne doit pas être annoncée comme une absence d'envoi.
              const uploadUnknown = isResponseLostFailure(upload.error);
              reportSaveFailure(
                `de l'envoi de ${upload.count} média(s) du point ${marker.id}`,
                upload.error,
                classifyFlushFailure(upload, uploadUnknown)
              );
            }
            if (replacement) {
              reportSaveFailure(
                `du nettoyage de ${replacement.count} ancien(s) média(s) du point ${marker.id}`,
                replacement.error,
                "replacement"
              );
            }
          }
          marker.media = mergePoiMediaUploadResults(
            marker.media,
            flushedMedia
          );
          // Republier le marqueur reconstruit (médias serveur + retrait
          // des previews legacy mortes) pour que l'écran de consultation
          // se mette à jour sans rechargement.
          const refreshedMarker = applyFlushedMediaToMarker(
            marker,
            flushedMedia
          );
          if (refreshedMarker) {
            refreshedMarkers.push(refreshedMarker);
          }
          // Nettoyage des images de QCM devenues orphelines, uniquement si
          // tous les envois de ce point ont abouti (la file ne doit plus
          // contenir de reprise). Un échec est journalisé, non bloquant.
          if (uploadFailures.length === 0) {
            const removedQcm = await cleanupOrphanQcmMedia(
              marker,
              projectDatas._id
            );
            if (removedQcm.length > 0) {
              const alreadyRefreshed = refreshedMarkers.find(
                (candidate) => candidate.id === marker.id
              );
              if (alreadyRefreshed) {
                // Retirer seulement les orphelins supprimés : le media
                // rafraîchi garde l'ordre de l'image principale.
                alreadyRefreshed.media = (alreadyRefreshed.media || []).filter(
                  (media) =>
                    !removedQcm.some(
                      (removed) =>
                        removed.mediaType === media.mediaType &&
                        removed.fileName === media.fileName
                    )
                );
              } else {
                refreshedMarkers.push({
                  ...marker,
                  media: [...(marker.media || [])],
                });
              }
            }
          }
        } catch (err) {
          // Marker identifié pour que le bilan soit exploitable (17 marqueurs
          // au plus) sans afficher 17 messages à l'utilisateur.
          //
          // REFUS CERTAIN vs ISSUE INCONNUE : un 4xx du serveur prouve que le
          // point n'a pas été écrit. Toute autre erreur (réponse perdue après
          // une création peut-être aboutie) doit rester INCERTAINE : annoncer
          // « non enregistré » dans ce cas était le défaut relevé par la revue
          // du 16 septembre, et il poussait l'utilisateur à recréer un point
          // déjà présent.
          //
          // RÉCONCILIATION IMPOSSIBLE : la création n'est PAS retentée
          // aveuglément (elle créerait un doublon si la précédente avait
          // abouti). Le point reste sans `serverId` : la sauvegarde suivante
          // refera la lecture d'abord.
          const poiRefused = isRefusedWrite(err);
          reportSaveFailure(
            `du marqueur ${marker.id}`,
            err,
            poiRefused ? "poi" : "poiUnknown"
          );
          // Les fichiers choisis pour ce point n'ont PAS pu être envoyés (le
          // flush n'a même pas lieu sans serverId) : ils restent dans la file
          // EN MÉMOIRE et un rechargement les perd. La faute du POI et celle
          // des fichiers sont deux conséquences distinctes du même incident.
          if (pendingPoiMediaCount(marker.id) > 0) {
            // Même distinction : si la création a pu aboutir, les fichiers ont
            // pu partir avec elle.
            reportSaveFailure(
              `des médias en attente du point ${marker.id}`,
              err,
              "pendingMedia"
            );
          }
        }
      })
    );

    try {
      const removalOutcome = await updateProjectMarkers(
        currentMarkers,
        projectDatas._id,
        reportSaveFailure,
        { removedServerIds, finalize: finalizeConfirmedPoiRemoval }
      );
      // FILE DE REPRISE DES SUPPRESSIONS : seules les suppressions
      // CONFIRMÉES de CE LOT sortent de la file (l'instantané
      // `removedServerIds` ci-dessus). Une demande AJOUTÉE pendant la
      // sauvegarde n'y figure pas : elle survit à cette confirmation et sera
      // traitée à la sauvegarde suivante. La finalisation locale
      // (retrait React, icône, files d'attente) est déjà faite par le
      // callback `finalize` au fil des confirmations.
      const confirmedNow = new Set(
        (removalOutcome.confirmedRemovals || []).map((id) => String(id))
      );
      if (confirmedNow.size > 0) {
        selectedMarkersToDeleteRef.current = selectedMarkersToDeleteRef.current.filter(
          (queued) => !confirmedNow.has(String(queued))
        );
      }
    } catch (err) {
      reportSaveFailure("des marqueurs du projet", err);
    }
    // C1 (parcours 2026-09-04) : sans setMarkers, la consultation garde
    // son rendu périmé jusqu'au rechargement du parcours.
    if (refreshedMarkers.length > 0) {
      setMarkers((previousMarkers) =>
        previousMarkers.map((previous) => {
          const refreshed = refreshedMarkers.find(
            (candidate) => candidate.id === previous.id
          );
          return refreshed || previous;
        })
      );
    }
    // sauvegarde des autres attributs du projet
    try {
      await roadmakerApi.projects.update(projectDatas._id, {
        originPOI: originMarker,
        destinationPOI: destinationMarker,
        customIndicationsEdited: customIndicationsEditedAtSave,
        destinationSameAsOrigin: destinationSameAsOrigin,
        kmlFile: kmlFile,
        urlKmlToFetsh: urlKmlToFetsh,
        // PROTECTION DU TRACÉ : le champ n'est envoyé que s'il a été recalculé
        // dans cette session. Sans lui, le serveur conserve son tracé — un
        // tracé vide ne peut donc plus en écraser un valide. Le retrait
        // VOLONTAIRE par l'utilisateur reste possible : il passe par un
        // recalcul, qui marque l'état comme modifié.
        // Sérialisation arrondie ({lat, lng} numériques) : l'état en mémoire
        // reste en objets LatLng pour les exporteurs (lat()/lng()).
        ...(trackDirtyAtSave
          ? {
              polyline_result: polyRef.current,
              trkseg: serializeDetailedTrack(trksegRef.current),
              // Flag persisté : au rechargement, l'éditeur saura que le
              // tracé principal vient d'un import (blocage calcul Google
              // + rendu Polyline), sans heuristique sur le trkseg.
              importedMainTrack: hasImportedMainTrackRef.current,
            }
          : {}),
        ...(importedTracksPayload !== undefined
          ? { importedTracks: importedTracksPayload }
          : {}),
        ...(navigationPointsChanged
          ? {
              customNavigationPoints: JSON.stringify(customNavigationPoints),
            }
          : {}),
      });
      savedImportedTracksRef.current = importedTracksAtSave;
      if (routeCalculationFailures.length === 0) {
        trackOutcome = SAVE_OUTCOMES.confirmed;
      }
      // Le tracé enregistré n'a plus besoin d'être renvoyé à la prochaine
      // sauvegarde (qui ne concerne alors que les points d'intérêt).
      if (
        routeCalculationFailures.length === 0 &&
        trackVersionRef.current === trackVersionAtSave
      ) {
        isTrackDirtyRef.current = false;
        setIsTrackDirty(false);
      }
      initialCustomIndicationsEditedRef.current =
        customIndicationsEditedAtSave;
    } catch (error) {
      // 4xx = refus EXPLICITE du serveur, seul cas où l'on peut affirmer que
      // rien n'a été enregistré. Tout le reste (coupure, délai, réponse
      // illisible) laisse l'issue INCONNUE.
      trackOutcome =
        error instanceof RoadmakerApiError &&
        error.status >= 400 &&
        error.status < 500
          ? SAVE_OUTCOMES.refused
          : SAVE_OUTCOMES.unknown;
      // `kind: "track"` : cet échec est DÉJÀ porté par `trackOutcome`, qui seul
      // distingue un refus explicite d'une réponse perdue. Le classer en
      // « autre » ajouterait un message générique redondant au bilan.
      reportSaveFailure("du tracé et des données du projet", error, "track");
    }

    // sauvegarde des attributs du User
    try {
      await roadmakerApi.users.update(userData._id, {
        iconIndexArray: iconIndexArray,
      });
    } catch (error) {
      reportSaveFailure("des préférences utilisateur", error);
    }

    // FIN DU CYCLE DE SAUVEGARDE — purge de l'ensemble des « confirmées non
    // appliquées », sous GARDE DE FIN DES UTILISATEURS de cet état.
    //
    // La fin de persistance ne suffit pas (vérification du 17 sept. :
    // chevauchement bouton/sauvegarde) : un DELETE lancé par le BOUTON peut
    // être encore en vol — son verrou (`pendingDeleteLocksRef`) a justement
    // fait passer la sauvegarde sans l'attendre, et sa finalisation
    // conservera l'instantané d'avant. On attend donc que TOUS les verrous
    // d'intention soient libérés : à cet instant, aucune opération connue ne
    // détient encore un ancien tableau, et l'état React a absorbé les
    // retraits (le filtrage, idempotent, ne modifiait de toute façon plus
    // un état propre).
    //
    // Attente bornée : un verrou qui ne se libérerait jamais (bug tiers) ne
    // doit pas suspendre la sauvegarde indéfiniment — au-delà du délai, on
    // CESSE D'ATTENDRE, et la purge est REPORTÉE au prochain cycle.
    //
    // PAS DE PURGE APRÈS EXPIRATION (correctif du 17 sept., vérification de
    // l'attente des verrous) : la première version sortait de la boucle par
    // `break` puis exécutait le `clear()` INCONDITIONNEL — l'ensemble était
    // donc vidé malgré le verrou, et la réponse TARDIVE du bouton (arrivée
    // après coup) recalculait depuis sa fermeture encore peuplée : le point
    // supprimé par CETTE sauvegarde réapparaissait dans la dernière requête.
    // Le message « purge reportée » décrivait alors l'inverse du code. Une
    // expiration ne prouve pas la fin des opérations : seule la libération
    // RÉELLE des verrous autorise la purge.
    const REMOVALS_PURGE_TIMEOUT_MS = 30000;
    const purgeStartedAt = Date.now();
    let purgeDeferred = false;
    while (pendingDeleteLocksRef.current.size > 0) {
      if (Date.now() - purgeStartedAt > REMOVALS_PURGE_TIMEOUT_MS) {
        purgeDeferred = true;
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    if (purgeDeferred) {
      console.warn(
        "[sauvegarde] purge des suppressions confirmées reportée au prochain cycle : opération(s) de suppression encore en cours."
      );
    } else {
      confirmedRemovalsNotYetAppliedRef.current.clear();
    }

    // Bilan combiné : TOUTES les catégories en échec y figurent (le tracé non
    // enregistré ne masque plus l'avertissement sur les fichiers restés en
    // mémoire). La règle vit dans `buildSaveReport`, couverte par des tests
    // unitaires.
    // Sauvegarde sans aucun échec : ce qui a été envoyé devient la référence.
    // Les POI dont les médias viennent d'être publiés y sont pris dans leur
    // version republiée (champs legacy nettoyés), comme dans l'état React ;
    // ceux dont la suppression a été confirmée en sont retirés.
    if (saveFailures.length === 0) {
      savedSnapshotRef.current = withoutSnapshotMarkers(
        withSnapshotMarkers(snapshotAtSave, refreshedMarkers),
        removedDuringSaveRef.current
      );
    }
    const summary = summarizeSaveFailures(saveFailures);
    console.error(
      `[sauvegarde] issue tracé : ${trackOutcome}, ${summary.count} échec(s) agrégé(s) :`,
      summary.scopes.join(", ")
    );
    return buildSaveReport({ trackOutcome, failures: saveFailures });
  };

  /**
   * Permet de sauvegarder les données du projet.
   *
   * Cette fonction ne fait QUE l'affichage et la séquence : elle doit garantir
   * que la boîte de chargement est refermée et que le verrou est libéré, même
   * si une opération lève une exception que le bilan n'attendait pas.
   *
   * PORTÉE DE CETTE GARANTIE : elle empêche qu'une exception NON PRÉVUE laisse
   * la boîte affichée et le bouton inactif. C'est une garantie de propreté de
   * l'interface, PAS l'explication de l'incident des 45 minutes : cet incident
   * n'est ni reproduit ni élucidé, et aucune cause ne lui est attribuée (revue
   * du 16 septembre, point Standards #5). Un `finally` ne s'exécute d'ailleurs
   * pas si la promesse ne se dénoue jamais, ce qui reste hors de cette
   * garantie.
   */
  const sendDataToServ = async () => {
    // Un seul enregistrement à la fois : le bouton reste actif pendant le PUT
    // et deux clics rapprochés envoyaient la même sauvegarde deux fois.
    if (savingRef.current) {
      return;
    }
    savingRef.current = true;
    let loadingOpen = false;
    try {
      const refusalKey = getSaveRefusalKey(originMarker, destinationMarker, markers);
      if (refusalKey) {
        DialogBox({
          text: t(refusalKey),
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
      LoadingBox({
        text: t("saveProject"),
        icon: "info",
      });
      loadingOpen = true;
      // Le rapport est traduit ICI, jamais à partir du corps d'une réponse
      // serveur (un proxy ou une panne peut renvoyer autre chose).
      const report = await persistProjectData();
      if (report?.status === "refused") {
        closeLoadingBox();
        loadingOpen = false;
        DialogBox({
          text: t(report.messageKey),
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
      if (!Array.isArray(report?.keys) || report.keys.length === 0) {
        return;
      }
      // Une seule catégorie : son message contient déjà tout le contexte. Plus
      // d'une : un en-tête commun puis les causes, faute de quoi l'utilisateur
      // ne verrait qu'une des situations (défaut du bilan exclusif).
      const message =
        report.keys.length > 1
          ? `${t(SAVE_REPORT_KEYS.message)} ${report.keys
              .map((key) => t(key))
              .join(" ")}`
          : t(report.keys[0]);
      // CONSEIL DE RECHARGEMENT : ajouté en complément, et SEULEMENT quand il
      // ne peut rien détruire (`buildSaveReport` l'a vérifié). Le texte
      // d'origine le proposait sans condition, y compris dans un bilan
      // signalant des fichiers restés en mémoire — suivre ce conseil les
      // perdait définitivement (défaut P1 de la revue du 16 septembre).
      const fullMessage = report.reloadAdvice
        ? `${message} ${t(SAVE_REPORT_KEYS.reloadAdvice)}`
        : message;
      customToastNotify("warning", fullMessage, undefined, {
        autoClose: report.pendingUploads ? 15000 : 8000,
        closeOnClick: false,
        toastId: "save-report",
      });
    } catch (error) {
      // Filet de sécurité : une exception inattendue ne doit jamais laisser le
      // chargement affiché ni le verrou posé. Les modifications restent en
      // mémoire, l'utilisateur peut relancer la sauvegarde.
      console.error("[sauvegarde] exception inattendue", error);
      customToastNotify("warning", t(SAVE_REPORT_KEYS.other), undefined, {
        autoClose: 8000,
        closeOnClick: false,
        toastId: "save-report",
      });
    } finally {
      if (loadingOpen) {
        closeLoadingBox();
      }
      savingRef.current = false;
    }
  };

  /**
   * Exporte le parcours
   */

  function exportParcours() {
    const parcours = {
      originPOI: originMarker,
      destinationPOI: destinationMarker,
      customIndicationsEdited: customIndicationsEdited,
      customNavigationPoints: customNavigationPoints,
      markers: markers,
    };

    // Convertir l'objet parcours en chaîne JSON
    const parcoursJSON = JSON.stringify(parcours);

    // Créer un lien de téléchargement pour le fichier JSON
    const downloadLink = document.createElement("a");
    downloadLink.href =
      "data:text/json;charset=utf-8," + encodeURIComponent(parcoursJSON);
    downloadLink.download = "parcours.json";

    // Simuler le clic sur le lien de téléchargement pour démarrer le téléchargement
    downloadLink.click();
  }

  /**
   * Permet de lancer l'exportation du projet, utilisation de la fonction/fichier ExportProject
   */
  const handleExport = (uploadToGithub) => {
    if (userPack === "Standard") {
      DialogBoxWithFooter({
        title: t("premiumFeatures"),
        text: t("exportPremium"),
        icon: "warning",
        confirmButtonText: "OK",
        footer: t("toBePremium"),
        footerOnClick: () => setVisibleComponent("Subscriptions"),
      });
      return;
    }
    ExportProject(
      projectDatas.name,
      markers,
      markerTypes,
      originMarker,
      destinationMarker,
      destinationSameAsOrigin,
      trkseg,
      importedTrack,
      projectDatas.projectType,
      uploadToGithub,
      customNavigationPoints
    );
  };

  /**
   * Permet d'envoyer le fichier GPX au serveur, utilisant la fonction/fichier SendGpx
   */

  const handleSendGpxToServ = () => {
    SendGpx(
      projectDatas.name,
      customNavigationPoints,
      markers,
      markerTypes,
      trkseg,
      projectDatas.projectType,
      projectDatas._id
    );
  };

  /**
   * Permet de changer la route sélectionnée
   * @param {object} directions
   */

  const handleRouteChange = (directions) => {
    // Récupérer l'index de l'itinéraire sélectionné dans l'objet DirectionsResult
    const newSelectedRouteIndex = directions.routes.findIndex(
      (route) => route === directions.routes[selectedRoute]
    );
    setSelectedRoute(newSelectedRouteIndex);
  };
  //ferme infoWindow
  const fermerInfoWindow = () => {
    setInfoCustomNavig("");
  };
  // recharger la map une fois que la position change
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      const center = new window.google.maps.LatLng(
        parseFloat(mapPosition.lat),
        parseFloat(mapPosition.lng)
      );
      setZoom(13);
    }
  }, [mapPosition]);

  useEffect(() => {
    const relevantMarkers = markers.filter(
      (marker) =>
        marker.type === markerTypes.origin ||
        marker.type === markerTypes.destination ||
        marker.type === markerTypes.step
    );

    const hasOrigin = relevantMarkers.some(
      (marker) => marker.type === markerTypes.origin
    );
    const hasDestination = relevantMarkers.some(
      (marker) => marker.type === markerTypes.destination
    );

    if (relevantMarkers.length === 0 || !hasOrigin || !hasDestination) return;

    const isInitialRouteLoad = skipRouteCalculationAfterLoadRef.current;
    skipRouteCalculationAfterLoadRef.current = false;

    return scheduleRouteCalculation(markers, {
      markDirty: !isInitialRouteLoad,
    });
  }, [
    // On surveille uniquement les markers pertinents, donc on filtre ici directement
    JSON.stringify(
      markers.filter(
        (marker) =>
          marker.type === markerTypes.origin ||
          marker.type === markerTypes.destination ||
          marker.type === markerTypes.step
      )
    ),
  ]);

  /**
   * Check si le téléchargement est possible
   * @returns {boolean} true si le téléchargement est possible, false sinon
   */
  const checkPossibleUpload = () => {
    if (downloadTime) {
      const currentTime = new Date();
      const timeDifference = currentTime - downloadTime;
      if (timeDifference < 5 * 60 * 1000) {
        LoadingBox({
          text: t("checkPossibleUpload"),
          icon: "warning",
        });
        setTimeout(() => {
          closeLoadingBox();
        }, 4000);
        return false;
      } else {
        return true;
      }
    }
    return true;
  };

  /**
   * Permet d'importer un fichier KML
   * @param {event} event - événement de clic sur le bouton d'importation de fichier KML
   */

  const handleKmlUpload = (event) => {
    event.target.value = "";
    DialogBox({
      text: "L’import KML sera réactivé avec le stockage OCI.",
      icon: "info",
      confirmButtonText: "OK",
    });
  };

  useEffect(() => {
    if (trkseg.length) {
      console.log("✅ Trkseg mis à jour :", trkseg);
    }
  }, [trkseg]);

  useEffect(() => {
    if (importedTrack.length) {
      console.log("🟥 Trace importée mise à jour :", importedTrack);
    }
  }, [importedTrack]);

  const handleResumeGoogleRoute = async () => {
    const confirmation = await Swal.fire({
      title: t("resumeGoogleTitle"),
      text: t("resumeGoogleText"),
      icon: "warning",
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: t("resumeGoogleConfirm"),
      cancelButtonText: t("gpxModeCancel"),
      confirmButtonColor: "#d33",
    });
    if (!isExplicitConfirmation(confirmation)) {
      return;
    }
    const result = await calculateRoute(markers, {
      ignoreImportedMainTrack: true,
    });
    // Drapeau baissé seulement si Google a remplacé le tracé (sinon l'import reste la référence en base).
    if (result?.status === "success") {
      hasImportedMainTrackRef.current = false;
      setHasImportedMainTrack(false);
      return;
    }
    DialogBox({
      text: t("resumeGoogleError"),
      icon: "error",
      confirmButtonText: "OK",
    });
  };

  const handleGpxUpload = async (event) => {
    const newSelectedFile = event.target.files[0];
    // Réarme l'input pour permettre de re-choisir le même fichier ensuite.
    event.target.value = "";
    console.log(
      "[handleGpxUpload] Fichier sélectionné :",
      newSelectedFile?.name
    );

    if (!checkPossibleUpload() || !newSelectedFile) {
      return;
    }

    let parsedPoints;
    try {
      parsedPoints = parseGpxTrack(await newSelectedFile.text());
    } catch (error) {
      DialogBox({
        text: t("gpxReadError"),
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    console.log(
      "[handleGpxUpload] Points extraits du GPX :",
      parsedPoints.length
    );
    if (!parsedPoints || parsedPoints.length < 2) {
      DialogBox({
        text: t("gpxNoTrack"),
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // Un itinéraire existe déjà (tracé Google calculé ou tracé importé en
    // remplacement) : demander le mode d'import. Sinon : remplacement
    // direct (premier tracé du projet).
    const hasExistingTrack = hasExistingGpxTrack(
      trksegRef.current,
      polyRef.current,
      importedTracksRef.current
    );
    let mode = "replace";
    if (hasExistingTrack) {
      mode = resolveGpxImportMode(await Swal.fire({
        title: t("gpxImportModeTitle"),
        text: t("gpxImportModeText"),
        icon: "question",
        showDenyButton: true,
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonText: t("gpxModeComplementary"),
        denyButtonText: t("gpxModeReplace"),
        cancelButtonText: t("gpxModeCancel"),
        confirmButtonColor: "#3085d6",
        denyButtonColor: "#d33",
      }));
    }

    if (mode === "cancel") {
      return;
    }

    if (mode === "complementary") {
      // MODE COMPLÉMENTAIRE : la trace s'ajoute en plus du tracé existant
      // (sentier que Google Maps ne connaît pas). Elle est dessinée et
      // persistée dans importedTracks ; le tracé principal n'est pas
      // touché, et le calcul Google reste possible sur celui-ci.
      importedTracksRef.current = [
        ...importedTracksRef.current,
        {
          id: `it-${Date.now()}`,
          name: newSelectedFile.name,
          points: parsedPoints,
        },
      ];
      setImportedTracks(importedTracksRef.current);
      DialogBox({
        text: t("gpxComplementaryAdded"),
        icon: "success",
        confirmButtonText: "OK",
      });
      return;
    }

    // MODE REMPLACEMENT : le GPX devient le tracé principal du parcours.
    // Alimente le pipeline moderne (trkseg + polyline_result) : le tracé
    // part à la sauvegarde et est servi au mobile. Les points de départ et
    // d'arrivée sont déduits des extrémités de la trace importée.
    importedTracksRef.current = [];
    setImportedTracks([]);
    // Un calcul Google en vol ne doit pas écraser le GPX importé.
    routeGenerationRef.current += 1;
    trackVersionRef.current += 1;
    isTrackDirtyRef.current = true;
    setIsTrackDirty(true);
    trksegRef.current = parsedPoints;
    setTrkseg(parsedPoints);
    polyRef.current = encodeDetailedTrack(parsedPoints);
    setDirectionsResponse(null);
    setCustomNavigationPoints([]);
    setCustomIndicationsEdited(false);
    setHasImportedMainTrack(true);
    setInitialNavigationButtonClass("invisibleButtons");

    // Départ/arrivée déduits des extrémités si absents (nécessaires à
    // RoadPlayer) ; sinon on ne touche jamais aux POI existants. Posés à la
    // FOIS dans les états dédiés (originPOI/destinationPOI du PUT projet)
    // ET dans le tableau markers : la carte ne dessine QUE markers, et la
    // sauvegarde en fait des documents POI réels (sans quoi départ/arrivée
    // restent invisibles à l'écran et côté RoadPlayer).
    if (!originMarker || Object.keys(originMarker).length === 0) {
      const startMarker = {
        id: generateRandomNumber(1, 1000000000),
        lat: parsedPoints[0].lat,
        lng: parsedPoints[0].lng,
        type: markerTypes.origin,
        title: START_POI,
        description: "",
        Project_id: projectDatas._id,
      };
      setOriginMarker(startMarker);
      setMarkers((previous) => [...previous, startMarker]);
    }
    if (!destinationMarker || Object.keys(destinationMarker).length === 0) {
      const endMarker = {
        id: generateRandomNumber(1, 1000000000),
        lat: parsedPoints[parsedPoints.length - 1].lat,
        lng: parsedPoints[parsedPoints.length - 1].lng,
        type: markerTypes.destination,
        title: END_POI,
        description: "",
        Project_id: projectDatas._id,
      };
      setDestinationMarker(endMarker);
      setMarkers((previous) => [...previous, endMarker]);
    }

    // Centre la carte sur la trace importée.
    const lats = parsedPoints.map((p) => p.lat);
    const lngs = parsedPoints.map((p) => p.lng);
    setMapPosition({
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    });
    DialogBox({
      text: t("gpxReplacedTrack"),
      icon: "success",
      confirmButtonText: "OK",
    });
  };

  /**
   * Permet d'importer un projet (format GPX) et de l'afficher sur la carte
   * @param {event} e - événement de clic sur le bouton d'importation de projet
   */

  const handleProjectUpload = (e) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const gpxContent = e.target.result;
        if (
          Object.keys(originMarker).length === 0 ||
          Object.keys(destinationMarker).length === 0
        ) {
          console.log("1");
          LoadingBox({
            text: t("loadingAdd"),
            icon: "info",
          });
          await calculateGPXRoute(
            gpxContent,
            projectDatas,
            markerTypes,
            setOriginMarker,
            setDestinationMarker,
            setMarkers,
            calculateRoute,
            markers
          );
          setShowInputGPX(false);
          closeLoadingBox();
        } else {
          console.log(originMarker + "ok" + destinationMarker);
          DialogBox({
            text: t("importErrorStartEnd"),
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      };
      reader.readAsText(file);
    }
  };

  // const premiumUser = userData.pack === "Premium" ? true : false;

  return (
    //passer les informations en utilisant le useContexte
    <UserContext.Provider
      value={{
        markers,
        setMarkers,
        selectedMarkerId,
        handleMarkerClick,
        handleMarkerDelete,
        customIndicationsEdited,
        setCustomIndicationsEdited,
        customNavigationPoints,
        setCustomNavigationPoints,
        setMapPosition,
        infoCustomNavig,
        setInfoCustomNavig,
        travelMode,
        setTravelMode,
        handleMenuItemClick,
        markerTypes,
        iconIndexArray,
        setIconIndexArray,
        userData,
      }}
    >
      <MapBoxSize zoom={zoom} setMapDimensions={setMapDimensions} />

      <div>
        <CoordinatesAlert copiedCoordinates={copiedCoordinates} />
        <div className="container-fluid">
          <div className="row">
            <div
              className={
                customNavigationPanelVisibilityIsActif ||
                navigationPanelVisibilityIsActif
                  ? "col-md-4"
                  : "col-md-6"
              }
            >
              <MapWithAMarker
                containerElement={
                  <div
                    className="carte"
                    style={{
                      height: mapDimensions.height,
                      width: mapDimensions.width,
                    }}
                    ref={mapRef}
                  />
                }
                mapElement={<div style={{ height: "100%" }} />}
                markers={markers}
                onMarkerDragEnd={handleMarkerDragEnd}
                onMarkerClick={handleMarkerClick}
                onMarkerClickDelete={handleMarkerDelete}
                onMapClick={handleMapClick}
                position={
                  mapPosition !== initialPosition
                    ? mapPosition
                    : { lat: latitude, lng: longitude }
                }
                zoom={markers.length ? zoom : zoom}
                directionsResponse={directionsResponse}
                mainTrack={hasImportedMainTrack ? trkseg : []}
                importedTracks={importedTracks}
                projectType={projectDatas.projectType}
                path={path}
                selectedRoute={selectedRoute}
                handleRouteChange={handleRouteChange}
                poiLabels={poiLabels}
                infoCustomNavig={infoCustomNavig}
                fermerInfoWindow={fermerInfoWindow}
                mapPosition={mapPosition}
                markerIcons={markerIcons}
                selectedMarkerId={selectedMarkerId}
                kmlFile={kmlFile}
                kmlLayerKey={kmlLayerKey}
              />
            </div>

            <div
              className={
                customNavigationPanelVisibilityIsActif ||
                navigationPanelVisibilityIsActif
                  ? "row col-md-8 mb-4 mx-auto mx-md-auto py-3 py-md-0"
                  : "row col-md-6 mb-4 mx-auto mx-md-auto py-3 py-md-0"
              }
              style={{ maxheight: "500px" }}
            >
              <PlacesAutocomplete
                value={placeInput ? placeInput : ""}
                onChange={(value) => setPlaceInput(value)}
                onSelect={handleSelect}
              >
                {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading,
                }) => (
                  <>
                    <div
                      className={
                        customNavigationPanelVisibilityIsActif ||
                        navigationPanelVisibilityIsActif
                          ? "containerCalculateRoute  col-md-6 "
                          : "containerCalculateRoute  col-md-12"
                      }
                    >
                      {/*getInputProps est une fonction qui prend en compte ce qu'on ecrit dans l'input de recherche d'adresse*/}
                      <div className="searchBarContainer">
                        <img
                          src={LOGO_CONNEXION_IMG}
                          alt="logo"
                          className="logoSearchBar"
                        />
                        <div className="searchBar">
                          <input
                            className="inputFindRoute"
                            {...getInputProps({
                              placeholder: t("searchAddress"),
                            })}
                          />
                          <button
                            className="buttonFindRoute"
                            onClick={handleFocus}
                          >
                            <i className="iconSearchRoute bi bi-search"></i>
                          </button>
                        </div>
                      </div>
                      {/*parmètrage des suggestions de lieux*/}
                      {loading && <div>Loading...</div>}
                      {suggestions.map((suggestion) => {
                        const style = suggestion.active
                          ? { backgroundColor: "#fafafa", cursor: "pointer" }
                          : { backgroundColor: "#ffffff", cursor: "pointer" };
                        return (
                          //la fonction getSuggestionItemProps prend en compte les suggestions de lieux qui seront affichés dans un span
                          <div
                            {...getSuggestionItemProps(suggestion, { style })}
                            key={suggestion.placeId}
                          >
                            <span>{suggestion.description}</span>
                          </div>
                        );
                      })}
                      <CalculateRouteButtons
                        selectedButtonStyle={selectedButtonStyle}
                        notSelectedButtonStyle={notSelectedButtonStyle}
                        travelMode={travelMode}
                        handleTravelMode={handleTravelMode}
                        calculateRoute={calculateRoute}
                        hasImportedMainTrack={hasImportedMainTrack}
                        onResumeGoogleRoute={handleResumeGoogleRoute}
                        setTravelMode={setTravelMode}
                      />
                      <MapBoxOptions
                        distance={distance}
                        duration={duration}
                        showAllLabels={showAllLabels}
                        hideAllLabels={hideAllLabels}
                        optimizeWaypoints={optimizeWaypoints}
                        setOptimizeWaypoints={setOptimizeWaypoints}
                        avoidHighways={avoidHighways}
                        setAvoidHighways={setAvoidHighways}
                        setPoilLabels={setPoilLabels}
                        kmlFile={kmlFile}
                      />
                      <MapBoxFeatures
                        refreshMap={refreshMap}
                        initialButtonNavigationsVisibility={
                          initialButtonNavigationsVisibility
                        }
                        customNavigationButtonClass={
                          customNavigationButtonClass
                        }
                        handleExport={handleExport}
                        handleSendGpxToServ={handleSendGpxToServ}
                        showNavigationPanel={showNavigationPanel}
                        showCustomNavigationPanel={showCustomNavigationPanel}
                        toggleCustomNavigationPanel={
                          toggleCustomNavigationPanel
                        }
                        toggleNavigationPanel={toggleNavigationPanel}
                        sendDataToServ={sendDataToServ}
                        originMarker={originMarker}
                        destinationMarker={destinationMarker}
                        markers={markers}
                        exportParcours={exportParcours}
                        handleKmlUpload={handleKmlUpload}
                        showInputKML={showInputKML}
                        setShowInputKML={setShowInputKML}
                        showInputGPX={showInputGPX}
                        setShowInputGPX={setShowInputGPX}
                        showInputGPXKml={showInputGPXKml}
                        setShowInputGPXKml={setShowInputGPXKml}
                        handleProjectUpload={handleProjectUpload}
                        handleGpxUpload={handleGpxUpload}
                        userPack={userPack}
                        setVisibleComponent={setVisibleComponent}
                      />
                    </div>
                    <div className=" col-md-6  mx-auto mx-md-auto py-3 py-md-0">
                      {customNavigationPanelVisibility.display === "block" &&
                        customNavigationPanelVisibilityIsActif && (
                          <div className="headerDirections">
                            <span className="spanTitleDirections col-12">
                              <h6 className="titleDirections">
                                {t("customDirections")}
                              </h6>
                            </span>
                          </div>
                        )}
                      {navigationPanelVisibility.display === "block" &&
                        navigationPanelVisibilityIsActif && (
                          <div className="headerDirections">
                            <span className="spanTitleDirections  col-12">
                              <h6 className="titleDirections">
                                {t("initialDirections")}
                              </h6>
                            </span>
                          </div>
                        )}

                      <div
                        id="directions-panel"
                        className="directionsContainer overflow-auto"
                        style={{
                          height: "482px",
                          width: "100%",
                          display: navigationPanelVisibilityIsActif
                            ? "block"
                            : "none",
                        }}
                      ></div>

                      <div
                        id="custom-directions-panel"
                        className="directionsContainer overflow-auto"
                        style={customNavigationPanelVisibility}
                      >
                        {customNavigationPanelVisibility.display === "block" &&
                          customNavigationPanelVisibilityIsActif && (
                            <CustomNavigation
                              directionsResponse={directionsResponse}
                              selectedRoute={selectedRoute}
                              kmlFile={kmlFile}
                              customIndicationsEdited={customIndicationsEdited}
                              setCustomIndicationsEdited={
                                setCustomIndicationsEdited
                              }
                              customNavigationPoints={customNavigationPoints}
                              setCustomNavigationPoints={
                                setCustomNavigationPoints
                              }
                              setMapPosition={setMapPosition}
                              setInfoCustomNavig={setInfoCustomNavig}
                            />
                          )}
                      </div>
                    </div>
                  </>
                )}
              </PlacesAutocomplete>
            </div>
          </div>
          <div className="row flex-column flex-md-row customRow">
            <div className="containerCalculateRoute h-100 col-12 col-md-4 customStepbox">
              <StepBox
                markers={markers}
                handleMarkerClick={handleMarkerClick}
                titleOfProject={titleOfProject}
                selectedMarkerId={selectedMarkerId}
                stepMarkers={stepMarkers}
                originMarker={originMarker}
                destinationMarker={destinationMarker}
              />
            </div>
            <div
              className={`col-${
                customNavigationPanelVisibilityIsActif ||
                navigationPanelVisibilityIsActif
                  ? "2 ms-5"
                  : "3"
              }`}
            ></div>

            <div
              className={`col-12 col-md-${
                customNavigationPanelVisibilityIsActif ||
                navigationPanelVisibilityIsActif
                  ? "3 ms-5"
                  : "4"
              } center-content`}
            >
              <Indice
                projectDatas={projectDatas}
                setEditedSubType={setEditedSubType}
                setMarkers={setMarkers}
                handleInputBlur={handleInputBlur}
                handleInputFocus={handleInputFocus}
                userData={userData}
              />
            </div>
          </div>
        </div>
        <ContextMenu
          menuVisible={menuVisible}
          projectDatas={projectDatas}
          markerTypes={markerTypes}
          originMarker={originMarker}
          destinationMarker={destinationMarker}
          menuPosition={menuPosition}
          handleMenuItemClick={handleMenuItemClick}
          handleClickOutsideMap={handleClickOutsideMap}
          setCopiedCoordinates={setCopiedCoordinates}
          setShowInfoPointMenu={setShowInfoPointMenu}
          projectType={projectDatas.projectType}
        />
      </div>
    </UserContext.Provider>
  );
}
